
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { useQuery } from "react-query";
import GeoServices from "@/CADServices/APIs/geo";
import {
  compareStrings,
  dropDownDataModel,
  dropDownDataModelForAptNo,
  handleNumberNoSpaceKeyDown,
  handleNumberTextKeyDown,
  handleNumDotNoSpaceKeyDown,
  isEmpty,
  isEmptyObject,
} from "@/CADUtils/functions/common";
import { toastifyError, toastifySuccess } from "@/Components/Common/AlertMsg";
import GEOContactTable from "@/CADComponents/GEOContactTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faUpload, faDownload, faTrash } from "@fortawesome/free-solid-svg-icons";
import { phoneTypes } from "@/CADUtils/constant";
import Location from "@/CADComponents/Common/Location";
import ClassNames from "classnames";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import useObjState from "@/CADHook/useObjState";
import ModalConfirm from "@/CADComponents/Common/ModalConfirm";
import { useSelector } from "react-redux";
import {
  getShowingDateText,
  getShowingWithOutTime,
  tableCustomStyles,
} from "@/Components/Common/Utility";
import DataTable from '@/Components/Common/DataTableWithAutoPagination';
import CallTakerServices from "@/CADServices/APIs/callTaker";
import MasterTableListServices from "@/CADServices/APIs/masterTableList";
import FlagTableModal from "@/CADComponents/FlagTableModal";
import FlagModal from "@/CADComponents/FlagMaster/FlagModal";
import Tooltip from "@/CADComponents/Common/Tooltip";
import CreatableSelect from "react-select/creatable";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import BootstrapTooltip from "react-bootstrap/Tooltip";
import {
  colourStyles,
  colourStylesNoReq,
  customStylesWithOutColorNew,
} from "@/CADComponents/Utility/CustomStylesForReact";
import PropTypes from "prop-types";
import { AddDelete_Img, fetchPostData, fetchPostWithTableData } from "@/Components/hooks/Api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const GEO_DOC_TYPES = [
  { value: "SOP", label: "SOP — Standard Operating Procedure" },
  { value: "FloorPlan", label: "Floor Plan" },
  { value: "Hazmat", label: "Hazmat Information Sheet" },
  { value: "Photo", label: "Site Photo" },
  { value: "KnoxBox", label: "Knox Box / Key Access" },
  { value: "Other", label: "Other" },
];


const parsePremisePipDate = (raw) => {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw !== "string") return null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) return new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2]));
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Normalize premiseAttr from API: ids as numbers; legacy Code strings coerced to id. */
const normalizePremiseAttrDropdowns = (raw, hazmatClassDropDown, structureTypesDropDown, sprinklerTypeDropDown, constructionDropDown) => {
  const base = { ...initialPremiseAttr, ...(raw && typeof raw === "object" ? raw : {}) };
  const toId = (val, options) => {
    if (val === null || val === undefined || val === "") return null;
    if (typeof val === "number" && Number.isFinite(val)) return val;
    if (typeof val === "string" && /^\d+$/.test(val)) return Number(val);
    if (typeof val === "string") {
      const byCode = options.find((o) => o.Code === val);
      return byCode ? byCode.id : null;
    }
    return null;
  };
  base.StructureTypeID = toId(base.StructureTypeID ?? base.structureTypeID ?? base.structureType, structureTypesDropDown);
  base.ConstructionTypeID = toId(base.ConstructionTypeID ?? base.constructionTypeID ?? base.constructionType, constructionDropDown);
  base.SprinklerTypeID = toId(base.SprinklerTypeID ?? base.sprinklerTypeID ?? base.sprinklerType, sprinklerTypeDropDown);
  base.HazmatClassID = toId(base.HazmatClassID ?? base.hazmatClassID ?? base.hazmatClass, hazmatClassDropDown);
  delete base.structureTypeID;
  delete base.constructionTypeID;
  delete base.sprinklerTypeID;
  delete base.hazmatClassID;
  delete base.structureType;
  delete base.constructionType;
  delete base.sprinklerType;
  delete base.hazmatClass;
  if ((base.KnoxBoxLocation == null || base.KnoxBoxLocation === "") && base.knoxLocation != null) {
    base.KnoxBoxLocation = base.knoxLocation;
  }
  delete base.knoxLocation;
  return base;
};

const initialPremiseAttr = {
  StructureTypeID: null,
  ConstructionTypeID: null,
  Stories: "",
  YearBuilt: "",
  RoofNotes: "",
  Sprinklers: null,
  SprinklerTypeID: null,
  FireAlarmSystem: null,
  KnoxBoxPresent: null,
  KnoxBoxLocation: "",
  HazmatOnFile: null,
  HazmatClassID: null,
  HazmatDescription: "",
  OccupancyDay: "",
  OccupancyNight: "",
  OccupancyNotes: "",
  HydrantNotes: "",
  AccessNotes: "",
  ConstraintNarrowAccess: false,
  ConstraintLowClearance: false,
  ConstraintWeightLimit: false,
  ConstraintAerialRestricted: false,
  ConstraintSteepGrade: false,
  ConstraintNotes: "",
  DispatcherNotes: "",
  PIPOnFile: null,
  PIPVersion: "",
  PIPLastUpdated: "",
  PIPDocumentRef: "",
};

/** Map GetPremiseDetailByAptID row (PascalCase) into premiseAttr (same PascalCase). */
const mapPremiseDetailRowToPremiseAttr = (row) => {
  if (!row || typeof row !== "object") return { ...initialPremiseAttr };
  const toStr = (v) => (v == null || v === "" ? "" : String(v));
  const yn = (v) => {
    if (v === true || v === false) return v;
    if (v === 1) return true;
    if (v === 0) return false;
    return null;
  };
  let pipLastUpdated = "";
  if (row.PIPLastUpdated != null && row.PIPLastUpdated !== "") {
    pipLastUpdated = getShowingWithOutTime(row.PIPLastUpdated);
  }

  return {
    StructureTypeID: row.StructureTypeID ?? null,
    ConstructionTypeID: row.ConstructionTypeID ?? null,
    Stories: toStr(row.Stories),
    YearBuilt: toStr(row.YearBuilt),
    RoofNotes: row.RoofNotes ?? "",
    Sprinklers: yn(row.Sprinklers),
    SprinklerTypeID: row.SprinklerTypeID ?? null,
    FireAlarmSystem: yn(row.FireAlarmSystem),
    KnoxBoxPresent: yn(row.KnoxBoxPresent),
    KnoxBoxLocation: row.KnoxBoxLocation ?? "",
    HazmatOnFile: yn(row.HazmatOnFile),
    HazmatClassID: row.HazmatClassID ?? null,
    HazmatDescription: row.HazmatDescription ?? "",
    OccupancyDay: row.OccupancyDay != null && row.OccupancyDay !== "" ? String(row.OccupancyDay) : "",
    OccupancyNight: row.OccupancyNight != null && row.OccupancyNight !== "" ? String(row.OccupancyNight) : "",
    OccupancyNotes: row.OccupancyNotes ?? "",
    HydrantNotes: row.HydrantNotes ?? "",
    AccessNotes: row.AccessNotes ?? "",
    ConstraintNarrowAccess: !!row.ConstraintNarrowAccess,
    ConstraintLowClearance: !!row.ConstraintLowClearance,
    ConstraintWeightLimit: !!row.ConstraintWeightLimit,
    ConstraintAerialRestricted: !!row.ConstraintAerialRestricted,
    ConstraintSteepGrade: !!row.ConstraintSteepGrade,
    ConstraintNotes: row.ConstraintNotes ?? "",
    DispatcherNotes: row.DispatcherNotes ?? "",
    PIPOnFile: yn(row.PIPOnFile),
    PIPVersion: row.PIPVersion ?? "",
    PIPLastUpdated: pipLastUpdated,
    PIPDocumentRef: row.PIPDocumentRef ?? "",
  };
};

/** AdvancedSearch Table row → premiseAttr shape (API fields overlap GetPremiseDetail; HazmatClass is free text → HazmatDescription). */
const mapAdvancedSearchRowToPremiseAttrShape = (row) => {
  if (!row || typeof row !== "object") return { ...initialPremiseAttr };
  const toStr = (v) => (v == null || v === "" ? "" : String(v));
  const yn = (v) => {
    if (v === true || v === false) return v;
    if (v === 1) return true;
    if (v === 0) return false;
    return null;
  };
  let pipLastUpdated = "";
  if (row.PIPLastUpdated != null && row.PIPLastUpdated !== "") {
    pipLastUpdated = getShowingWithOutTime(row.PIPLastUpdated);
  }
  const hazmatDesc =
    row.HazmatDescription != null && row.HazmatDescription !== ""
      ? String(row.HazmatDescription)
      : row.HazmatClass != null && row.HazmatClass !== ""
        ? String(row.HazmatClass).trim()
        : "";

  return {
    ...initialPremiseAttr,
    StructureTypeID: row.StructureTypeID ?? null,
    Stories: toStr(row.Stories),
    YearBuilt: toStr(row.YearBuilt),
    Sprinklers: yn(row.Sprinklers),
    SprinklerTypeID: row.SprinklerTypeID ?? null,
    HazmatOnFile: yn(row.HazmatOnFile),
    HazmatClassID: row.HazmatClassID ?? null,
    HazmatDescription: hazmatDesc,
    OccupancyDay: row.OccupancyDay != null && row.OccupancyDay !== "" ? String(row.OccupancyDay) : "",
    OccupancyNight: row.OccupancyNight != null && row.OccupancyNight !== "" ? String(row.OccupancyNight) : "",
    PIPOnFile: yn(row.PIPOnFile),
    PIPVersion: row.PIPVersion ?? "",
    PIPLastUpdated: pipLastUpdated,
    PIPDocumentRef: row.PIPDocumentRef ?? "",
  };
};

/** True only when premise-detail row contains meaningful survey data. */
const hasPremiseDetailData = (row) => {
  if (!row || typeof row !== "object") return false;
  const triStateKeys = new Set(["Sprinklers", "FireAlarmSystem", "KnoxBoxPresent", "HazmatOnFile", "PIPOnFile"]);
  const keysToCheck = [
    "StructureTypeID",
    "Stories",
    "YearBuilt",
    "ConstructionTypeID",
    "RoofNotes",
    "Sprinklers",
    "SprinklerTypeID",
    "FireAlarmSystem",
    "KnoxBoxPresent",
    "KnoxBoxLocation",
    "HazmatOnFile",
    "HazmatClassID",
    "HazmatDescription",
    "OccupancyDay",
    "OccupancyNight",
    "OccupancyNotes",
    "HydrantNotes",
    "AccessNotes",
    "ConstraintNarrowAccess",
    "ConstraintLowClearance",
    "ConstraintWeightLimit",
    "ConstraintAerialRestricted",
    "ConstraintSteepGrade",
    "ConstraintNotes",
    "DispatcherNotes",
    "PIPOnFile",
    "PIPVersion",
    "PIPLastUpdated",
    "PIPDocumentRef",
  ];
  return keysToCheck.some((k) => {
    const v = row[k];
    if (v == null) return false;
    if (typeof v === "string") return v.trim() !== "";
    if (typeof v === "number") return Number.isFinite(v);
    if (typeof v === "boolean") {
      if (triStateKeys.has(k)) return true;
      return v === true;
    }
    return true;
  });
};

// ─── Tab identifiers ──────────────────────────────────────────────────────────
const TABS = {
  HOME: "home",
  CONTACT: "contactInfo",
  DOCUMENTS: "documents",    // ★ GEO REVAMP - NEW TAB
  FLAG_HISTORY: "flagHistory",
};

// ─────────────────────────────────────────────────────────────────────────────
const GEOModal = (props) => {
  const localStoreData = useSelector((state) => state.Agency.localStoreData);
  const {
    openGEOModal,
    setOpenGEOModal,
    setSelectedButton = () => { },
    unVerifiedSingleLocationsList,
    refetchUnVerifiedLocationsData = () => { },
    isVerifiedPage = false,
    onLocationConfirmed = () => { },
  } = props;

  const [loginPinID, setLoginPinID] = useState("");
  const [loginAgencyID, setLoginAgencyID] = useState("");

  const [premiseDropDown, setPremiseDropDown] = useState([]);
  const [flagDropDown, setFlagDropDown] = useState([]);
  const [aptSuiteNoDropDown, setAptSuiteNoDropDown] = useState([]);
  const [jurisdictionDropDown, setJurisdictionDropDown] = useState([]);
  const [constructionDropDown, setConstructionDropDown] = useState([]);
  const [structureTypesDropDown, setStructureTypesDropDown] = useState([]);
  const [sprinklerTypeDropDown, setSprinklerTypeDropDown] = useState([]);
  const [hazmatClassDropDown, setHazmatClassDropDown] = useState([]);



  const [patrolZoneDropDown, setPatrolZoneDropDown] = useState([]);
  const [fireZoneDropDown, setFireZoneDropDown] = useState([]);
  const [emsZoneDropDown, setEmsZoneDropDown] = useState([]);
  const [mutualAidZoneDropDown, setMutualAidZoneDropDown] = useState([]);
  const [towZoneDropDown, setTowZoneDropDown] = useState([]);
  const [otherZoneDropDown, setOtherZoneDropDown] = useState([]);
  const [gcModifierDropDown, setGCModifierDropDown] = useState([]);

  const [locationStatus, setLocationStatus] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(0);
  const [geoLocationID, setGeoLocationID] = useState();
  const [onSelectLocation, setOnSelectLocation] = useState(true);
  const [locationList, setLocationList] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSelectLocation, setIsSelectLocation] = useState(false);
  const [isChangeFields, setIsChangeFields] = useState(false);
  const [isContactChangeFields, setIsContactChangeFields] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [zoom, setZoom] = useState(17);
  const [showPage, setShowPage] = useState(TABS.HOME);
  const [disabled, setDisabled] = useState(false);
  const [isGoogleLocation, setIsGoogleLocation] = useState(true);
  const [isMapVisible, setIsMapVisible] = useState(false);

  // ── Apt / unit state ───────────────────────────────────────────────────────
  const [aptData, setAptData] = useState({});
  const [aptInputValue, setAptInputValue] = useState("");

  // ── Flag state ─────────────────────────────────────────────────────────────
  const [flagBudgeList, setFlagBudgeList] = useState([]);
  const [flagName, setFlagName] = useState("");
  const [selectedFlagData, setSelectedFlagData] = useState({});
  const [openAddFlagModal, setOpenAddFlagModal] = useState(false);
  const [openFlagTableModal, setOpenFlagTableModal] = useState(false);
  const [flagHistoryList, setFlagHistoryList] = useState([]);
  // ★ GEO REVAMP — Document state ────────────────────────────────────────────
  // ── Advanced Search state ─────────────────────────────────────────────────
  const [openAdvancedSearch, setOpenAdvancedSearch] = useState(false);
  const initialAdvFilters = {
    AddressContains: "",
    IsVerified: null,       // null = Any | true = Yes | false = No (JSON to AdvancedSearch)
    PremiseTypes: "",
    FireZone: "",
    PIPOnFile: null,
    HazmatOnFile: null,
    Sprinklers: null,
    HasConstraints: null,
    StoriesMin: "",
    StoriesMax: "",
    ModifiedBefore: "",
    PageNumber: 1,
    PageSize: 50,
  };
  const [advFilters, setAdvFilters] = useState(initialAdvFilters);
  const [advResults, setAdvResults] = useState([]);
  const [advLoading, setAdvLoading] = useState(false);
  const [advSearched, setAdvSearched] = useState(false);
  /** Which quick-filter chip is active (visual); cleared when filters are edited manually */
  const [advActiveQuickFilterId, setAdvActiveQuickFilterId] = useState(null);

  const handleAdvFilterChange = (field, value) => {
    setAdvActiveQuickFilterId(null);
    setAdvFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdvSearch = async (filtersOverride) => {
    const filters = filtersOverride || advFilters;
    setAdvLoading(true);
    setAdvSearched(true);
    try {
      const response = await GeoServices.advancedSearch({
        AgencyID: loginAgencyID,
        ...filters,
      });
      let rows = [];
      try {
        const parsed = JSON.parse(response?.data?.data || "{}");
        rows = Array.isArray(parsed) ? parsed : (parsed?.Table || []);
      } catch { rows = []; }
      setAdvResults(rows);
    } catch {
      setAdvResults([]);
    } finally {
      setAdvLoading(false);
    }
  };

  const handleAdvClearFilters = () => {
    setAdvFilters(initialAdvFilters);
    setAdvResults([]);
    setAdvSearched(false);
    setAdvActiveQuickFilterId(null);
  };

  const handleAdvQuickFilter = (quickFilterId, preset) => {
    const f = { ...initialAdvFilters, ...preset };
    setAdvFilters(f);
    setAdvActiveQuickFilterId(quickFilterId);
    handleAdvSearch(f);
  };

  const [documentList, setDocumentList] = useState([]);  // loaded from DB
  const [docUploadState, setDocUploadState] = useState({
    documentType: null,   // Select from GEO_DOC_TYPES
    documentName: "",     // Free text name / title
    notes: "",     // Optional notes about this document
    file: null,   // File object from input[type=file]
  });
  const [hasSopDocuments, setHasSopDocuments] = useState(false); // drives SOP badge on home tab
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const docFileInputRef = useRef(null); // ref for the hidden file input

  // ── Modal confirm state ────────────────────────────────────────────────────
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // ── Map ref ────────────────────────────────────────────────────────────────
  const mapRef = useRef(null);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_ADD_API_KEY,
  });

  // ── Contact form state ────────────────────────────────────────────────────
  const initialValueContact = {
    LastName: "",
    MiddleName: "",
    FirstName: "",
    PhoneType: "",
    PhoneNo: "",
  };
  const [contactInformation, setContactInformation] = useState(initialValueContact);
  const [contactList, setContactList] = useState([]);

  const [
    errorContactInformation,
    ,
    handleErrorContactInformation,
    clearStateContactInformation,
  ] = useObjState({ PhoneNo: "", LastName: "" });

  // ── Main GEO form state ────────────────────────────────────────────────────
  const buildInitialFormValues = () => ({
    PremiseNo: "",
    stDirection: "",
    Street: "",
    stDirection2: "",
    ApartmentNo: "",
    commonPlaceName: "",
    premiseType: null,
    City: "",
    ZipCode: "",
    mileMarker: "",
    coordinateX: "",
    coordinateY: "",
    AltStreet: "",
    jurisdiction: [],   // ★ GEO REVAMP: was single value, now array for multi-jurisdiction
    intersection1: "",
    intersection2: "",
    verify: false,
    // ★ GEO REVAMP: each zone is its own typed dropdown (not a shared pool)
    patrolZone: null,
    fireZone: null,
    emsZone: null,
    mutualAidZone: null,   // ★ GEO REVAMP - NEW
    towZone: null,   // ★ GEO REVAMP - NEW
    otherZone: null,
    beatNumber: "",     // ★ GEO REVAMP - NEW: law enforcement beat number
    // ★ FIX: GCModifier select state — stores the selected dropdown option
    // Shape: { value: GCModifierID, label: 'CODE — Desc', code: GCModifierCode }
    // Cleared by handleClear(). Restored from DB in the unVerifiedSingleLocationsList effect.
    gcModifier: null,
    currentFlag: null,
    location: "",
    IsVerify: false,
    // Field-level lock flags (set when Google geocodes a field — user can't overwrite)
    isStreet: false, isCity: false, isPremiseNo: false, isZipCode: false,
    isMileMarker: false, isCommonPlaceName: false, isStDirection: false,
    isStDirection2: false, isIntersection1: false, isIntersection2: false,
    isAltStreet: false, isApartmentNo: false, isCoordinateX: false,
    isCoordinateY: false, isUpdate: false,
    premiseAttr: { ...initialPremiseAttr },
  });


  const [geoFormValues, setGEOFormValues] = useState(buildInitialFormValues);
  const [premiseSubTab, setPremiseSubTab] = useState("building");
  const updatePremiseAttr = useCallback((patch) => {
    setGEOFormValues((prev) => ({
      ...prev,
      premiseAttr: {
        ...initialPremiseAttr,
        ...(prev.premiseAttr || {}),
        ...patch,
      },
    }));
    setIsChangeFields(true);
  }, []);
  const premiseTabDots = useMemo(() => {
    const p = geoFormValues.premiseAttr || { ...initialPremiseAttr };
    const yn = (v) => v === true || v === false;
    const dot = (complete, partial) => {
      if (complete) return "complete";
      if (partial) return "partial";
      return "";
    };
    const accessFilled = !!(
      (p.HydrantNotes || "").trim()
      || (p.AccessNotes || "").trim()
      || (p.ConstraintNotes || "").trim()
      || (p.DispatcherNotes || "").trim()
      || p.ConstraintNarrowAccess
      || p.ConstraintLowClearance
      || p.ConstraintWeightLimit
      || p.ConstraintAerialRestricted
      || p.ConstraintSteepGrade
    );
    return {
      building: dot(
        !!p.StructureTypeID,
        !!(p.StructureTypeID || p.ConstructionTypeID || p.Stories || p.YearBuilt || (p.RoofNotes || "").trim()),
      ),
      protection: dot(
        yn(p.Sprinklers),
        yn(p.Sprinklers) || yn(p.FireAlarmSystem) || yn(p.KnoxBoxPresent) || !!p.SprinklerTypeID || !!(p.KnoxBoxLocation || "").trim(),
      ),
      hazmat: dot(
        yn(p.HazmatOnFile),
        yn(p.HazmatOnFile) || !!p.HazmatClassID || !!(p.HazmatDescription || "").trim(),
      ),
      occupancy: dot(
        !!(String(p.OccupancyDay || "").trim() || String(p.OccupancyNight || "").trim()),
        !!(String(p.OccupancyDay || "").trim() || String(p.OccupancyNight || "").trim() || (p.OccupancyNotes || "").trim()),
      ),
      access: dot(accessFilled, accessFilled),
      pip: dot(
        yn(p.PIPOnFile),
        yn(p.PIPOnFile) || !!(p.PIPVersion || "").trim() || !!(p.PIPLastUpdated || "").trim() || !!(p.PIPDocumentRef || "").trim(),
      ),
    };
  }, [geoFormValues.premiseAttr]);

  const matchZoneOption = useCallback((pool, apiValue) => {
    if (apiValue == null || apiValue === "") return null;
    if (!pool?.length) return null;
    const s = String(apiValue).trim();
    const exact = pool.find((z) => z.label === s);
    if (exact) return exact;
    const ci = pool.find((z) => (z.label || "").toLowerCase() === s.toLowerCase());
    if (ci) return ci;
    const incl = pool.find((z) => {
      const lbl = z.label || "";
      return lbl.includes(s) || (lbl && s.includes(lbl));
    });
    if (incl) return incl;
    return pool.find((z) => (z.zoneCode || z.code || "") === s) || null;
  }, []);

  const applyAdvancedSearchRow = useCallback(async (row) => {
    if (!row?.GeoLocationID) return;
    setOpenAdvancedSearch(false);
    setGeoLocationID(row.GeoLocationID);
    setIsSelectLocation(true);
    setShowPage(TABS.HOME);
    setPremiseSubTab("building");

    const aptNo = row.ApartmentNo != null && row.ApartmentNo !== "" ? String(row.ApartmentNo) : "";
    setAptData({
      value: aptNo,
      label: aptNo || " ",
      aptId: row.AptID ?? "",
    });

    const premiseNormalized = normalizePremiseAttrDropdowns(
      mapAdvancedSearchRowToPremiseAttrShape(row),
      hazmatClassDropDown,
      structureTypesDropDown,
      sprinklerTypeDropDown,
      constructionDropDown,
    );

    setGEOFormValues({
      ...buildInitialFormValues(),
      Id: row.GeoLocationID,
      location: row.Location ?? "",
      PremiseNo: row.PremiseNo ?? "",
      Street: row.Street ?? "",
      City: row.City ?? "",
      ZipCode: row.ZipCode != null && row.ZipCode !== "" ? String(row.ZipCode) : "",
      commonPlaceName: row.CommonPlace ?? "",
      ApartmentNo: aptNo,
      premiseType: premiseDropDown.find((p) => p.label === row.PremiseType) || null,
      patrolZone: matchZoneOption(patrolZoneDropDown, row.PatrolZone),
      fireZone: matchZoneOption(fireZoneDropDown, row.FireZone),
      emsZone: matchZoneOption(emsZoneDropDown, row.EMSZone),
      mutualAidZone: null,
      towZone: null,
      otherZone: null,
      gcModifier: row.GCModifierCode
        ? gcModifierDropDown.find((opt) => opt.code === row.GCModifierCode) || null
        : null,
      IsVerify: row.IsVerified === true || row.IsVerified === 1 || row.IsVerified === "1",
      coordinateX: "",
      coordinateY: "",
      premiseAttr: premiseNormalized,
      isUpdate: true,
      isStreet: !!(row.Street && String(row.Street).trim()),
      isCity: !!(row.City && String(row.City).trim()),
      isPremiseNo: !!(row.PremiseNo && String(row.PremiseNo).trim()),
      isZipCode: !!(row.ZipCode != null && String(row.ZipCode).trim()),
      isCommonPlaceName: !!(row.CommonPlace && String(row.CommonPlace).trim()),
      isApartmentNo: !!aptNo,
      isCoordinateX: false,
      isCoordinateY: false,
    });

    setIsChangeFields(true);

    try {
      const response = await GeoServices.getLocationDataByID({
        GeoLocationID: row.GeoLocationID,
        AgencyID: loginAgencyID,
      });
      const data = JSON.parse(response?.data?.data)?.Table?.[0];
      if (!data) return;

      setGEOFormValues((prev) => ({
        ...prev,
        coordinateX: data.Longitude != null && data.Longitude !== "" ? String(data.Longitude) : prev.coordinateX,
        coordinateY: data.Latitude != null && data.Latitude !== "" ? String(data.Latitude) : prev.coordinateY,
        stDirection: data.DirectionPrefix ?? prev.stDirection,
        stDirection2: data.DirectionSufix ?? prev.stDirection2,
        mileMarker: data.MileMaker != null && data.MileMaker !== "" ? String(data.MileMaker) : prev.mileMarker,
        AltStreet: data.AltStreet ?? prev.AltStreet,
        intersection1: data.InterDirectionPrefix ?? prev.intersection1,
        intersection2: data.InterDirectionSufix ?? prev.intersection2,
        jurisdiction: data.JurisdictionIDs?.length
          ? jurisdictionDropDown.filter((j) => data.JurisdictionIDs.includes(j.ID))
          : prev.jurisdiction,
        mutualAidZone: mutualAidZoneDropDown.find((z) => z.label === data.MutualAidZone) || prev.mutualAidZone,
        towZone: towZoneDropDown.find((z) => z.label === data.TowZone) || prev.towZone,
        otherZone: otherZoneDropDown.find((z) => z.label === data.OtherZone) || prev.otherZone,
        beatNumber: data.BeatNumber ?? prev.BeatNumber,
        currentFlag: data?.CurrentFlage?.length
          ? flagDropDown?.filter((item) => data.CurrentFlage.includes(item.label)) ?? prev.currentFlag
          : prev.currentFlag,
        isCoordinateX: !!(data.Longitude != null && String(data.Longitude).trim()) || prev.isCoordinateX,
        isCoordinateY: !!(data.Latitude != null && String(data.Latitude).trim()) || prev.isCoordinateY,
        isMileMarker: !!(data.MileMaker != null && String(data.MileMaker).trim()) || prev.isMileMarker,
        isStDirection: !!(data.DirectionPrefix && String(data.DirectionPrefix).trim()) || prev.isStDirection,
        isStDirection2: !!(data.DirectionSufix && String(data.DirectionSufix).trim()) || prev.isStDirection2,
        isIntersection1: !!(data.InterDirectionPrefix && String(data.InterDirectionPrefix).trim()) || prev.isIntersection1,
        isIntersection2: !!(data.InterDirectionSufix && String(data.InterDirectionSufix).trim()) || prev.isIntersection2,
        isAltStreet: !!(data.AltStreet && String(data.AltStreet).trim()) || prev.isAltStreet,
      }));
    } catch (e) {
      console.error("applyAdvancedSearchRow getLocationDataByID:", e);
    }
  }, [
    loginAgencyID,
    premiseDropDown,
    patrolZoneDropDown,
    fireZoneDropDown,
    emsZoneDropDown,
    gcModifierDropDown,
    hazmatClassDropDown,
    structureTypesDropDown,
    sprinklerTypeDropDown,
    constructionDropDown,
    jurisdictionDropDown,
    mutualAidZoneDropDown,
    towZoneDropDown,
    otherZoneDropDown,
    flagDropDown,
    matchZoneOption,
  ]);

  const [
    errorGeo,
    ,
    handleErrorGeo,
    clearState,
  ] = useObjState({
    location: false,
    patrolZone: false,
    emsZone: false,
    fireZone: false,
    otherZone: false,
    coordinateX: false,
    jurisdiction: false,
    coordinateY: false,
  });
  // ─────────────────────────────────────────────────────────────────────────
  // MAP
  // ─────────────────────────────────────────────────────────────────────────
  const mapCenter = {
    lng: parseFloat(geoFormValues?.coordinateX) || -98.5795,
    lat: parseFloat(geoFormValues?.coordinateY) || 39.8283,
  };

  const locations = [{
    lng: parseFloat(geoFormValues?.coordinateX) || -98.5795,
    lat: parseFloat(geoFormValues?.coordinateY) || 39.8283,
    status: "open",
    name: geoFormValues?.location,
  }];

  const getColorByStatus = (status) => {
    switch (status) {
      case "open": return "green";
      case "closed": return "red";
      case "pending": return "yellow";
      default: return "blue";
    }
  };

  const handleMapLoad = (map) => {
    mapRef.current = map;
    setTimeout(() => window.google.maps.event.trigger(map, "resize"), 200);
  };

  useEffect(() => {
    if (openGEOModal && isLoaded) {
      setTimeout(() => {
        setIsMapVisible(true);
        if (mapRef.current) window.google.maps.event.trigger(mapRef.current, "resize");
      }, 300);
    } else {
      setIsMapVisible(false);
    }
  }, [openGEOModal, isLoaded]);

  // ─────────────────────────────────────────────────────────────────────────
  // BOOTSTRAP — load auth from Redux
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (localStoreData) {
      setLoginPinID(localStoreData?.PINID);
      setLoginAgencyID(localStoreData?.AgencyID);
    }
  }, [localStoreData]);

  // ─────────────────────────────────────────────────────────────────────────
  // ZONE DROPDOWNS — separate fetch per zone type
  // ★ GEO REVAMP: Instead of one flat geoZoneDropDown used for all 4 selects,
  // we now fetch each zone type separately using the patched Sp_CAD_ZoneMaster
  // GetDataDropDown_Zone action with @ZoneType filter.
  // This prevents wrong zone types from appearing in the wrong dropdown.
  // ─────────────────────────────────────────────────────────────────────────
  const buildZoneDropDown = (data) =>
    (data || []).map((z) => ({
      value: z.ZoneID,
      // Show ESN number for fire zones if available
      label: z.ESNNumber
        ? `ESN-${z.ESNNumber} — ${z.ZoneDescription || z.ZoneCode}`
        : (z.ZoneDescription || z.ZoneCode),
      zoneCode: z.ZoneCode,
    }));

  // Fetch all zone types from single API response:
  // /CAD/GeoPetrolZone/GetZoneDetailsByType returns Table..Table5 for each type.
  const fetchZonesByType = useCallback(async () => {
    if (!loginAgencyID) return;
    try {
      const data = await fetchPostWithTableData("/CAD/GeoPetrolZone/GetZoneDetailsByType", {
        AgencyID: loginAgencyID,
      });

      setFireZoneDropDown(buildZoneDropDown(data?.Table || []));           // Fire
      setTowZoneDropDown(buildZoneDropDown(data?.Table1 || []));           // Tow
      setEmsZoneDropDown(buildZoneDropDown(data?.Table2 || []));           // EMS
      setMutualAidZoneDropDown(buildZoneDropDown(data?.Table3 || []));     // MutualAid
      setOtherZoneDropDown(buildZoneDropDown(data?.Table4 || []));         // Other
      setPatrolZoneDropDown(buildZoneDropDown(data?.Table5 || []));        // Patrol / Law
    } catch (e) {
      console.error("Error fetching zone details by type:", e);
      setFireZoneDropDown([]);
      setTowZoneDropDown([]);
      setEmsZoneDropDown([]);
      setMutualAidZoneDropDown([]);
      setOtherZoneDropDown([]);
      setPatrolZoneDropDown([]);
    }
  }, [loginAgencyID]);

  // Build GCModifier dropdown from DB
  const buildGCModifierDropDown = (data) =>
    (data || []).map(item => ({
      value: item.GCModifierID,
      label: `${item.GCModifierCode} \u2014 ${item.Description}`,
      code: item.GCModifierCode,
    }));

  useEffect(() => {
    if (!loginAgencyID || !openGEOModal) return;
    MasterTableListServices.getData_DropDown_GCModifier({ AgencyID: loginAgencyID })
      .then((res) => {
        const data = JSON.parse(res?.data?.data || '{}');
        setGCModifierDropDown(buildGCModifierDropDown(data?.Table || []));
      })
      .catch(() => setGCModifierDropDown([]));
  }, [loginAgencyID, openGEOModal]);

  useEffect(() => {
    if (!loginAgencyID || !openGEOModal) return;
    fetchZonesByType();
  }, [loginAgencyID, openGEOModal, fetchZonesByType]);

  // ─────────────────────────────────────────────────────────────────────────
  // JURISDICTION DROPDOWN
  // Fetches jurisdiction list. MasterJurisdiction already has
  // HoursOfOperationFrom/To — we use these to compute which jurisdiction
  // is currently active and highlight it.
  // ─────────────────────────────────────────────────────────────────────────
  const jurisdictionDataKey = `/CAD/MasterJurisdiction/GetDataDropDownJurisdiction/${loginAgencyID}`;
  const { data: getJurisdictionData, isSuccess: isFetchJurisdictionData } = useQuery(
    [jurisdictionDataKey, { AgencyID: loginAgencyID }],
    MasterTableListServices.getDataDropDownJurisdiction,
    { refetchOnWindowFocus: false, enabled: openGEOModal && !!loginAgencyID }
  );

  useEffect(() => {
    if (getJurisdictionData && isFetchJurisdictionData) {
      const data = JSON.parse(getJurisdictionData?.data?.data || "{}");
      setJurisdictionDropDown(data?.Table || []);
    } else {
      setJurisdictionDropDown([]);
    }
  }, [getJurisdictionData, isFetchJurisdictionData]);

  const buildDropDown = (data, id, label, code) =>
    (data || []).map(item => ({
      value: item[id],
      label: `${item[label]}`,
      code: item[code || ""],
    }));

  const constructionDataKey = `/CAD/Construction/GetDataDropDown_Construction/${loginAgencyID}`;
  const { data: getConstructionData, isSuccess: isFetchConstructionData } = useQuery(
    [constructionDataKey, { AgencyID: loginAgencyID }],
    MasterTableListServices.getDataDropDown_Construction,
    { refetchOnWindowFocus: false, enabled: !!loginAgencyID }
  );

  useEffect(() => {
    if (getConstructionData && isFetchConstructionData) {
      const data = JSON.parse(getConstructionData?.data?.data || "{}");
      setConstructionDropDown(buildDropDown(data?.Table, "ConstructionID", "Description"));
    } else {
      setConstructionDropDown([]);
    }
  }, [getConstructionData, isFetchConstructionData]);

  const sprinklerTypeDataKey = `/CAD/SprinklerType/GetDataDropDown_SprinklerType/${loginAgencyID}`;
  const { data: getSprinklerTypeData, isSuccess: isFetchSprinklerTypeData } = useQuery(
    [sprinklerTypeDataKey, { AgencyID: loginAgencyID }],
    MasterTableListServices.getDataDropDown_SprinklerType,
    { refetchOnWindowFocus: false, enabled: !!loginAgencyID }
  );

  useEffect(() => {
    if (getSprinklerTypeData && isFetchSprinklerTypeData) {
      const data = JSON.parse(getSprinklerTypeData?.data?.data || "{}");
      setSprinklerTypeDropDown(buildDropDown(data?.Table, "SprinklerTypeID", "Description"));
    } else {
      setSprinklerTypeDropDown([]);
    }
  }, [getSprinklerTypeData, isFetchSprinklerTypeData]);

  const structureTypesDataKey = `/CAD/StructureTypes/GetDataDropDown_StructureTypes/${loginAgencyID}`;
  const { data: getStructureTypesData, isSuccess: isFetchStructureTypesData } = useQuery(
    [structureTypesDataKey, { AgencyID: loginAgencyID }],
    MasterTableListServices.getDataDropDown_StructureTypes,
    { refetchOnWindowFocus: false, enabled: !!loginAgencyID }
  );

  useEffect(() => {
    if (getStructureTypesData && isFetchStructureTypesData) {
      const data = JSON.parse(getStructureTypesData?.data?.data || "{}");
      setStructureTypesDropDown(buildDropDown(data?.Table, "StructureTypeID", "Description"));
    } else {
      setStructureTypesDropDown([]);
    }
  }, [getStructureTypesData, isFetchStructureTypesData]);


  const hazmatClassDataKey = `/CAD/HazmatClass/GetDataDropDown_HazmatClass/${loginAgencyID}`;
  const { data: getHazmatClassData, isSuccess: isFetchHazmatClassData } = useQuery(
    [hazmatClassDataKey, { AgencyID: loginAgencyID }],
    MasterTableListServices.getHazmatClassDropDown,
    { refetchOnWindowFocus: false, enabled: !!loginAgencyID }
  );

  useEffect(() => {
    if (getHazmatClassData && isFetchHazmatClassData) {
      const data = JSON.parse(getHazmatClassData?.data?.data || "{}");
      setHazmatClassDropDown(buildDropDown(data?.Table, "value", "label", "code"));
    } else {
      setHazmatClassDropDown([]);
    }
  }, [getHazmatClassData, isFetchHazmatClassData]);

  // ★ GEO REVAMP: Determine which jurisdiction is currently active based on
  // HoursOfOperationFrom/To vs current time.
  // Returns true if the jurisdiction is active right now.
  const isJurisdictionActiveNow = (jur) => {
    if (!jur?.HoursOfOperationFrom || !jur?.HoursOfOperationTo) return true; // no hours = always active
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    const from = new Date(jur.HoursOfOperationFrom);
    const to = new Date(jur.HoursOfOperationTo);
    const fromMins = from.getHours() * 60 + from.getMinutes();
    const toMins = to.getHours() * 60 + to.getMinutes();
    if (fromMins <= toMins) return mins >= fromMins && mins <= toMins;
    // Overnight span (e.g. 22:00 to 06:00)
    return mins >= fromMins || mins <= toMins;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // PREMISE DROPDOWN
  // ─────────────────────────────────────────────────────────────────────────
  const getPremiseKey = `/CAD/GeoPremiseType/GetData_Premise`;
  const { data: premiseData, isSuccess: isFetchPremiseData } = useQuery(
    [getPremiseKey, {}],
    GeoServices.getPremise,
    { refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if (isFetchPremiseData && premiseData) {
      const data = JSON.parse(premiseData?.data?.data)?.Table || [];
      setPremiseDropDown(dropDownDataModel(data, "ID", "PremiseType"));
    }
  }, [isFetchPremiseData, premiseData]);

  // ─────────────────────────────────────────────────────────────────────────
  // FLAG DROPDOWN
  // ─────────────────────────────────────────────────────────────────────────
  const getFlagKey = `/CAD/GeoFlage/GetData_Flag`;
  const { data: flagData, isSuccess: isFetchFlagData } = useQuery(
    [getFlagKey, {}],
    GeoServices.getFlag,
    { refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if (isFetchFlagData && flagData) {
      const data = JSON.parse(flagData?.data?.data)?.Table || [];
      setFlagDropDown(dropDownDataModel(data, "ID", "CurrentFlag"));
    }
  }, [isFetchFlagData, flagData]);

  // ─────────────────────────────────────────────────────────────────────────
  // APT / SUITE DROPDOWN
  // ─────────────────────────────────────────────────────────────────────────
  const aptSuiteNoPayload = { AgencyID: loginAgencyID, GeoLocationID: geoLocationID };
  const aptSuiteNoKey = `/CAD/GeoLocation/Get_GeoLocationApartmentNo`;
  const {
    data: aptSuiteNoData,
    isSuccess: isFetchAptSuiteNoData,
    refetch: refetchAptSuiteNoData,
  } = useQuery(
    [aptSuiteNoKey, { aptSuiteNoPayload }],
    CallTakerServices.getAptSuiteNo,
    { refetchOnWindowFocus: false, enabled: openGEOModal && !!geoLocationID }
  );

  const defaultOption = aptSuiteNoDropDown.find(
    (option) => option?.aptId && !option?.value && !option?.label
  );

  useEffect(() => {
    if (isFetchAptSuiteNoData && aptSuiteNoData?.data?.data && isSelectLocation && geoLocationID && geoFormValues?.location) {
      const parsedData = JSON.parse(aptSuiteNoData.data.data || "{}");
      setAptSuiteNoDropDown(
        parsedData?.Table?.length
          ? dropDownDataModelForAptNo(parsedData.Table, "Description", "Description", "AptID")
          : []
      );
    } else {
      setAptInputValue("");
    }
  }, [isFetchAptSuiteNoData, aptSuiteNoData, geoLocationID, geoFormValues?.location]);

  useEffect(() => {
    if (!isSelectLocation && !geoFormValues?.location) {
      setGEOFormValues((prev) => ({
        ...prev,
        ApartmentNo: "",
        premiseAttr: { ...initialPremiseAttr },
      }));
      setPremiseSubTab("building");
      setAptSuiteNoDropDown([]);
    }
  }, [isFetchAptSuiteNoData, aptSuiteNoData, geoLocationID, geoFormValues?.location]);

  useEffect(() => {
    if (!aptData?.value && !aptData?.label && defaultOption) {
      setAptData({ value: "", label: "", aptId: defaultOption.aptId });
    }
  }, [aptSuiteNoDropDown]);

  // ─────────────────────────────────────────────────────────────────────────
  // FLAG BADGE LIST (for Home tab)
  // ─────────────────────────────────────────────────────────────────────────
  const flagPayload = {
    AptID: aptData?.aptId,
    FlagFromId: geoLocationID,
    FlagFrom: "GEO",
    AgencyID: loginAgencyID,
    Action: "Get_All_On_CallTaker",
  };

  const getFlagListKey = `/CAD/Flag/GetFlag`;
  const { data: getFlagList, refetch: getFlagListRefetch, isSuccess: isFetchGetFlagList } = useQuery(
    [getFlagListKey, { flagPayload }],
    CallTakerServices.getFlag,
    {
      refetchOnWindowFocus: false,
      retry: 0,
      enabled: openGEOModal && !!geoLocationID && !isEmpty(aptData?.aptId),
    }
  );

  useEffect(() => {
    if (geoFormValues?.location && isSelectLocation && isFetchGetFlagList && getFlagList && (isEmptyObject(aptData) || aptData?.aptId)) {
      const data = JSON.parse(getFlagList?.data?.data) || [];
      setFlagBudgeList(data);
    } else {
      setFlagBudgeList([]);
      setSelectedFlagData({});
    }
  }, [isFetchGetFlagList, getFlagList, geoLocationID, aptData]);


  const GetPremiseDetailByAptIDPayload = {
    AptID: aptData?.aptId,
    // FlagFromId: geoLocationID,
    AgencyID: loginAgencyID,
  };

  const getPremiseDetailByAptIDKey = `/CAD/GeoLocation/GetPremiseDetailByAptID`;
  const { data: getPremiseDetailByAptID, refetch: getPremiseDetailByAptIDRefetch, isSuccess: isFetchGetPremiseDetailByAptID } = useQuery(
    [getPremiseDetailByAptIDKey, { GetPremiseDetailByAptIDPayload }],
    CallTakerServices.GetPremiseDetailByAptID,
    {
      refetchOnWindowFocus: false,
      retry: 0,
      enabled: openGEOModal && !!geoLocationID && !isEmpty(aptData?.aptId),
    }
  );

  useEffect(() => {
    if (!isFetchGetPremiseDetailByAptID || !getPremiseDetailByAptID) return;
    let parsed = {};
    try {
      parsed = JSON.parse(getPremiseDetailByAptID?.data?.data || "{}");
    } catch {
      return;
    }
    const table = Array.isArray(parsed) ? parsed : parsed?.Table;
    if (!Array.isArray(table)) return;
    const row = table[0];
    if (!row) return;
    if (!hasPremiseDetailData(row)) return;
    const mapped = normalizePremiseAttrDropdowns(mapPremiseDetailRowToPremiseAttr(row), hazmatClassDropDown, structureTypesDropDown, sprinklerTypeDropDown, constructionDropDown);
    setGEOFormValues((prev) => ({ ...prev, premiseAttr: mapped }));
  }, [isFetchGetPremiseDetailByAptID, getPremiseDetailByAptID, aptData?.aptId]);

  // ─────────────────────────────────────────────────────────────────────────
  // FLAG HISTORY LIST
  // ─────────────────────────────────────────────────────────────────────────
  const flagHistoryPayload = { FlagFromId: geoLocationID, AptID: aptData?.aptId, AgencyID: loginAgencyID };
  const getFlagHistoryDataKey = `/CAD/Flag/Flags_History`;
  const { data: flagHistoryData, isSuccess: isFetchFlagHistoryData, refetch: refetchFlagHistory } = useQuery(
    [getFlagHistoryDataKey, { payload: flagHistoryPayload }],
    GeoServices.getFlagHistoryList,
    {
      refetchOnWindowFocus: false,
      enabled: showPage === TABS.FLAG_HISTORY && !!loginAgencyID && !!geoLocationID,
      retry: 0,
    }
  );

  useEffect(() => {
    if (isFetchFlagHistoryData && flagHistoryData) {
      const data = JSON.parse(flagHistoryData?.data?.data || "{}");
      setFlagHistoryList(data?.Table || []);
    } else {
      setFlagHistoryList([]);
    }
  }, [isFetchFlagHistoryData, flagHistoryData]);

  // ─────────────────────────────────────────────────────────────────────────
  // CONTACTS
  // ★ GEO REVAMP: Contacts now work at LOCATION level (GeoLocationID),
  // not just at apartment level (AptID). The contact tab is no longer
  // gated behind apt selection — any verified location can have contacts.
  // ─────────────────────────────────────────────────────────────────────────

  // Apt-level contacts (original behaviour, preserved)
  const getContactDetailApartmentNoKey = [
    `/CAD/GeoLocation/GetContactDetailApartmentNo`,
    { AptID: aptData?.aptId, AgencyID: loginAgencyID },
  ];
  const { data: contactDetailApartmentNo, isSuccess: isFetchContactDetailApartmentNo } = useQuery(
    getContactDetailApartmentNoKey,
    GeoServices.getContactDetailApartmentNo,
    {
      refetchOnWindowFocus: false,
      enabled: !!aptData?.aptId,
      retry: 0,
      onSuccess: (res) => {
        if (res?.data?.Data?.length === 0) {
          setContactList([]);
        } else {
          try {
            const parsedData = JSON.parse(res?.data?.data);
            setContactList(parsedData?.Table || []);
          } catch (e) {
            console.error("Error parsing contacts:", e);
          }
        }
      },
      onError: () => setContactList([]),
    }
  );

  // ★ GEO REVAMP: Also load location-level contacts when no apt selected.
  // Uses the same GeoLocationContactDetail endpoint but with GeoLocationID.
  useEffect(() => {
    if (!geoLocationID || aptData?.aptId) return; // apt-level takes precedence
    fetchPostData("/CAD/GeoLocationContactDetail/GetData_GeoContact", {
      GeoLocationID: geoLocationID,
      AgencyID: loginAgencyID,
    }).then((res) => {
      if (res) setContactList(res);
    }).catch(() => setContactList([]));
  }, [geoLocationID, aptData?.aptId]);

  // ─────────────────────────────────────────────────────────────────────────
  // ★ GEO REVAMP — DOCUMENT LIST
  // Fetches attached documents for this location (SOP, floor plans etc.)
  // Uses new CAD_GeoLocationDocument table via Sp_CAD_GeoLocationDocument.
  // ─────────────────────────────────────────────────────────────────────────
  const loadDocuments = useCallback(() => {
    if (!geoLocationID || !loginAgencyID) return;
    fetchPostData("/CAD/GeoDocument/GetDataGeoDocuments", {
      GeoID: geoLocationID,
      AgencyID: loginAgencyID,
    }).then((res) => {
      if (res) {
        setDocumentList(res);
        setHasSopDocuments(res.some((d) => d.DocumentType === "SOP"));
      }
    }).catch(() => setDocumentList([]));
  }, [geoLocationID, loginAgencyID]);

  useEffect(() => {
    if (showPage === TABS.DOCUMENTS && geoLocationID) loadDocuments();
  }, [showPage, geoLocationID]);

  // ─────────────────────────────────────────────────────────────────────────
  // LOCATION DATA (for search autocomplete popover)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const response = await GeoServices.getLocationData({
          Location: geoFormValues?.location,
          AgencyID: loginAgencyID,
        });
        const data = JSON.parse(response?.data?.data)?.Table || [];
        setLocationData(data);
      } catch (e) {
        console.error("Error fetching location data:", e);
        setLocationList([]);
        setLocationData([]);
      }
    };
    if (geoFormValues?.location) fetchLocationData();
  }, [geoFormValues?.location, isSelectLocation]);

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD UNVERIFIED LOCATION FOR EDITING
  // ─────────────────────────────────────────────────────────────────────────
  // ★ GEO REVAMP: Also restores new fields (beatNumber, mutualAidZone, towZone,
  // jurisdiction as array).
  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      if (!unVerifiedSingleLocationsList?.ID) return;
      try {
        setGeoLocationID(unVerifiedSingleLocationsList?.ID);
        setIsSelectLocation(true);
        const response = await GeoServices.getLocationDataByID({
          GeoLocationID: unVerifiedSingleLocationsList?.ID,
          AgencyID: loginAgencyID,
        });
        const data = JSON.parse(response?.data?.data)?.Table?.[0];
        if (!mounted || !data) return;

        let premiseAttrLoaded = { ...initialPremiseAttr };
        const rawPA = data?.PremiseAttributesJson;
        if (rawPA) {
          try {
            const o = typeof rawPA === "string" ? JSON.parse(rawPA) : rawPA;
            if (o && typeof o === "object") {
              premiseAttrLoaded = normalizePremiseAttrDropdowns(
                o,
                hazmatClassDropDown,
                structureTypesDropDown,
                sprinklerTypeDropDown,
                constructionDropDown,
              );
            }
          } catch (e) {
            /* ignore malformed JSON */
          }
        }




        setGEOFormValues({
          IsUsLocation: "Y",
          Street: data?.Street,
          Id: data?.ID || "",
          City: data?.City,
          PremiseNo: data?.PremiseNo,
          ZipCode: data?.ZipCode,
          mileMarker: data?.MileMaker || "",
          commonPlaceName: data?.CommonPlace,
          stDirection: data?.DirectionPrefix,
          stDirection2: data?.DirectionSufix,
          intersection1: data?.InterDirectionPrefix || "",
          intersection2: data?.InterDirectionSufix || "",
          AltStreet: data?.AltStreet,
          ApartmentNo: data?.ApartmentNo,
          coordinateX: data?.Longitude,
          coordinateY: data?.Latitude,
          location: data?.Location,
          currentFlag: flagDropDown?.filter((item) => data?.CurrentFlage?.includes(item.label)),
          premiseType: premiseDropDown?.find((item) => item?.label === data?.PremiseType),

          // ★ GEO REVAMP: Restore zone selections from type-specific pools
          patrolZone: patrolZoneDropDown.find((z) => z.label === data?.PatrolZone) || null,
          fireZone: fireZoneDropDown.find((z) => z.label === data?.FireZone) || null,
          emsZone: emsZoneDropDown.find((z) => z.label === data?.EMSZone) || null,
          mutualAidZone: mutualAidZoneDropDown.find((z) => z.label === data?.MutualAidZone) || null,
          towZone: towZoneDropDown.find((z) => z.label === data?.TowZone) || null,
          otherZone: otherZoneDropDown.find((z) => z.label === data?.OtherZone) || null,

          // ★ GEO REVAMP: Restore beat number
          beatNumber: data?.BeatNumber || "",

          // Restore gcModifier from DB code
          gcModifier: data?.GCModifierCode
            ? (gcModifierDropDown.find((opt) => opt.code === data.GCModifierCode) || null)
            : null,

          // ★ GEO REVAMP: Restore jurisdiction as array (multi-jurisdiction)
          jurisdiction: data?.JurisdictionIDs
            ? jurisdictionDropDown.filter((j) => data.JurisdictionIDs.includes(j.ID))
            : [],

          // Field lock flags
          isStreet: !!data?.Street,
          isCity: !!data?.City,
          isPremiseNo: !!data?.PremiseNo,
          isZipCode: !!data?.ZipCode,
          isMileMarker: !!data?.MileMaker,
          isCommonPlaceName: !!data?.CommonPlace,
          isStDirection: !!data?.DirectionPrefix,
          isStDirection2: !!data?.DirectionSufix,
          isIntersection1: !!data?.InterDirectionPrefix,
          isIntersection2: !!data?.InterDirectionSufix,
          isAltStreet: !!data?.AltStreet,
          isApartmentNo: !!data?.ApartmentNo,
          isCoordinateX: !!data?.Longitude,
          isCoordinateY: !!data?.Latitude,
          isUpdate: true,
          IsVerify: data?.IsVerified === 1,
          premiseAttr: premiseAttrLoaded,
        });
        setPremiseSubTab("building");
      } catch (e) {
        console.error("Error loading location for edit:", e);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, [unVerifiedSingleLocationsList, openGEOModal,
    patrolZoneDropDown, fireZoneDropDown, emsZoneDropDown,
    mutualAidZoneDropDown, towZoneDropDown, otherZoneDropDown, gcModifierDropDown,
    hazmatClassDropDown, structureTypesDropDown, sprinklerTypeDropDown, constructionDropDown,
    premiseDropDown, flagDropDown, jurisdictionDropDown, loginAgencyID]);

  // ─────────────────────────────────────────────────────────────────────────
  // FORM HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGEOFormValues((prev) => ({ ...prev, [name]: value }));
    setIsChangeFields(true);
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setGEOFormValues((prev) => ({ ...prev, [name]: selectedOption }));
    setIsChangeFields(true);
  };

  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactInformation((prev) => ({ ...prev, [name]: value }));
    if (name === "LastName" && errorContactInformation.LastName) {
      handleErrorContactInformation("LastName", "");
    }
  };

  const handleChangePhoneNumber = (e) => {
    const { name, value } = e.target;
    let ele = value.replace(/\D/g, "");
    if (ele.length === 10) {
      const match = ele.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        setContactInformation((prev) => ({
          ...prev,
          [name]: `${match[1]}-${match[2]}-${match[3]}`,
        }));
      }
    } else {
      ele = value.split("-").join("").replace(/\D/g, "");
      setContactInformation((prev) => ({ ...prev, [name]: ele }));
    }
    if (errorContactInformation.PhoneNo) handleErrorContactInformation("PhoneNo", "");
  };

  const handleSelectPhoneType = (selectedOption, { name }) => {
    setContactInformation((prev) => ({ ...prev, [name]: selectedOption }));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // APT HANDLERS (unchanged from original)
  // ─────────────────────────────────────────────────────────────────────────
  const handleSelectAptSuitNo = (selectedOption, { name }) => {
    if (selectedOption) {
      setGEOFormValues((prev) => ({ ...prev, [name]: selectedOption?.value || "" }));
      setAptData(selectedOption);
    } else if (defaultOption) {
      setGEOFormValues((prev) => ({ ...prev, [name]: "" }));
      setAptData({ value: "", label: "", aptId: defaultOption.aptId });
    } else {
      setGEOFormValues((prev) => ({ ...prev, [name]: "" }));
      setAptData({ value: "", label: "", aptId: "" });
    }
    setIsChangeFields(true);
    setAptInputValue("");
  };

  const handleCreateOption = (val) => {
    if (/^[a-zA-Z0-9]{1,4}$/.test(val)) {
      const newOption = { value: val, label: val, aptId: "" };
      setAptSuiteNoDropDown((prev) => [...prev, newOption]);
      handleSelectAptSuitNo(newOption, { name: "ApartmentNo" });
      setAptInputValue("");
    }
  };

  const handleAptInputChange = (value, { action }) => {
    if (action === "input-change" && /^[a-zA-Z0-9]{0,4}$/.test(value)) {
      setAptInputValue(value);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // KEYBOARD HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    const cc = e.keyCode || e.which;
    const controlKeys = [8, 9, 13, 27, 37, 38, 39, 40, 46];
    const isAlpha = (cc >= 65 && cc <= 90) || (cc >= 97 && cc <= 122);
    if (!isAlpha && !controlKeys.includes(cc)) e.preventDefault();
    if ((cc >= 48 && cc <= 57) || (cc >= 96 && cc <= 111)) e.preventDefault();
  };

  const handleSpecialKeyDown = (e) => {
    if (!(e.key.length === 1 && e.key.match(/[a-zA-Z0-9]/)) && e.key !== "Backspace") {
      e.preventDefault();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // VALIDATION
  // ─────────────────────────────────────────────────────────────────────────
  const validateGeoFormValues = () => {
    let isError = false;
    const keys = Object.keys(errorGeo);
    keys.forEach((field) => {
      if (field === "location" && isEmpty(geoFormValues[field])) {
        handleErrorGeo(field, true); isError = true;
      } else if (
        ["fireZone", "emsZone", "otherZone", "patrolZone"].includes(field) &&
        isEmptyObject(geoFormValues[field])
      ) {
        handleErrorGeo(field, true); isError = true;
      } else if (field === "jurisdiction" && (!geoFormValues.jurisdiction || geoFormValues.jurisdiction.length === 0)) {
        handleErrorGeo(field, true); isError = true;
      } else {
        handleErrorGeo(field, false);
      }
    });
    return !isError;
  };

  const validateContactFormValues = () => {
    const { PhoneType, PhoneNo } = contactInformation;
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    if (PhoneType?.label === "Cellular") {
      const hasError = !PhoneNo || !phoneRegex.test(PhoneNo);
      handleErrorContactInformation("PhoneNo", hasError);
      return !hasError;
    }
    handleErrorContactInformation("PhoneNo", false);
    return true;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CREATE LOCATION PAYLOAD
  // ★ GEO REVAMP: Now includes new fields: beatNumber, mutualAidZone, towZone,
  // jurisdiction as comma-separated IDs (multi-jurisdiction).
  // ─────────────────────────────────────────────────────────────────────────
  const createLocationPayload = () => {
    const premiseAttr = geoFormValues?.premiseAttr || {};
    const {
      Street = "", stDirection = "", stDirection2 = "", City = "", ZipCode = "",
      PremiseNo = "", ApartmentNo = "", commonPlaceName = "", premiseType = {},
      coordinateX = "", coordinateY = "", mileMarker = "", AltStreet = "",
      intersection1 = "", intersection2 = "", patrolZone = {}, emsZone = {},
      fireZone = {}, otherZone = {}, mutualAidZone = {}, towZone = {},
      IsVerify = "", location = "", currentFlag = [], jurisdiction = [],
      beatNumber = "",
    } = geoFormValues || {};

    return {
      ...(geoLocationID && { ID: geoLocationID, ModifiedByUserFK: loginPinID }),
      Street,
      DirectionPrefix: stDirection,
      DirectionSufix: stDirection2,
      City,
      ZipCode,
      PremiseNo,
      ApartmentNo,
      CommonPlace: commonPlaceName,
      PremiseType: premiseType?.label || "",
      Latitude: coordinateY,
      Longitude: coordinateX,
      MileMaker: mileMarker,
      // ★ GEO REVAMP: jurisdiction now an array → send as comma-separated IDs
      JurisdictionIDs: Array.isArray(jurisdiction) ? jurisdiction.map((j) => j.ID).join(",") : "",
      AltStreet,
      InterDirectionPrefix: intersection1,
      InterDirectionSufix: intersection2,
      PatrolZone: patrolZone?.label || "",
      EMSZone: emsZone?.label || "",
      FireZone: fireZone?.label || "",
      OtherZone: otherZone?.label || "",
      // ★ GEO REVAMP: new zone fields
      MutualAidZone: mutualAidZone?.label || "",
      TowZone: towZone?.label || "",
      // ★ GEO REVAMP: beat number
      BeatNumber: beatNumber || "",
      // ★ FIX: Send the GCModifierCode string, not the ID.
      // The DB column CAD_GeoLocation.GCModifierCode stores the code string.
      // The RunCard engine looks up GCModifierID from this code at call time.
      GCModifierCode: geoFormValues.gcModifier?.code || "",
      IsVerified: IsVerify,
      Location: location,
      CurrentFlage: currentFlag?.map((item) => item?.label).join(", "),
      CreatedByUserFK: geoLocationID ? undefined : loginPinID,
      AgencyID: loginAgencyID,
      // Extended premise survey fields sent as direct payload keys (not JSON string).
      StructureTypeID: premiseAttr.StructureTypeID ?? null,
      ConstructionTypeID: premiseAttr.ConstructionTypeID ?? null,
      Stories: premiseAttr.Stories ?? "",
      YearBuilt: premiseAttr.YearBuilt ?? "",
      RoofNotes: premiseAttr.RoofNotes ?? "",
      Sprinklers: premiseAttr.Sprinklers ?? null,
      SprinklerTypeID: premiseAttr.SprinklerTypeID ?? null,
      FireAlarmSystem: premiseAttr.FireAlarmSystem ?? null,
      KnoxBoxPresent: premiseAttr.KnoxBoxPresent ?? null,
      KnoxBoxLocation: premiseAttr.KnoxBoxLocation ?? "",
      HazmatOnFile: premiseAttr.HazmatOnFile ?? null,
      HazmatClassID: premiseAttr.HazmatClassID ?? null,
      HazmatDescription: premiseAttr.HazmatDescription ?? "",
      OccupancyDay: premiseAttr.OccupancyDay ?? "",
      OccupancyNight: premiseAttr.OccupancyNight ?? "",
      OccupancyNotes: premiseAttr.OccupancyNotes ?? "",
      HydrantNotes: premiseAttr.HydrantNotes ?? "",
      AccessNotes: premiseAttr.AccessNotes ?? "",
      ConstraintNarrowAccess: premiseAttr.ConstraintNarrowAccess ?? false,
      ConstraintLowClearance: premiseAttr.ConstraintLowClearance ?? false,
      ConstraintWeightLimit: premiseAttr.ConstraintWeightLimit ?? false,
      ConstraintAerialRestricted: premiseAttr.ConstraintAerialRestricted ?? false,
      ConstraintSteepGrade: premiseAttr.ConstraintSteepGrade ?? false,
      ConstraintNotes: premiseAttr.ConstraintNotes ?? "",
      DispatcherNotes: premiseAttr.DispatcherNotes ?? "",
      PIPOnFile: premiseAttr.PIPOnFile ?? null,
      PIPVersion: premiseAttr.PIPVersion ?? "",
      PIPLastUpdated: premiseAttr.PIPLastUpdated ?? "",
      PIPDocumentRef: premiseAttr.PIPDocumentRef ?? "",
      AptID: aptData?.aptId ?? null,
    };
  };
  const locationPayload = createLocationPayload();
  // ─────────────────────────────────────────────────────────────────────────
  // SAVE LOCATION
  // ★ GEO REVAMP: After save, calls onLocationConfirmed so CallTaker map updates.
  // ─────────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validateGeoFormValues()) return;
    setDisabled(true);
    const locationPayload = createLocationPayload();
    const response = await GeoServices.insertLocation(locationPayload);

    if (response?.data?.success) {
      if (geoLocationID) {
        toastifySuccess("Data Updated Successfully");
      } else {
        const data = JSON.parse(response?.data?.data);
        const newID = data?.Table?.[0]?.GeoLocationID;
        setGeoLocationID(newID);
        toastifySuccess("Data Saved Successfully");
      }

      // ★ GEO REVAMP: Notify parent (CallTaker) with confirmed coordinates
      // so the map pin updates immediately without closing anything.
      onLocationConfirmed({
        location: geoFormValues.location,
        latitude: geoFormValues.coordinateY,
        longitude: geoFormValues.coordinateX,
        geoLocationID: geoLocationID,
      });

      if (isVerifiedPage) onCloseLocation();
      refetchUnVerifiedLocationsData();
      setIsChangeFields(false);
    }
    setDisabled(false);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CONTACT SAVE
  // ★ GEO REVAMP: Now saves at GeoLocationID level when no apt selected,
  // falls back to AptID level when apt is selected (original behaviour).
  // ─────────────────────────────────────────────────────────────────────────
  const handleContactSave = async () => {
    const contactDetailPayload = {
      // ★ GEO REVAMP: Use GeoLocationID as fallback when no apt selected
      AptID: aptData?.aptId || null,
      GeoLocationID: geoLocationID,
      AgencyID: loginAgencyID,
      CreatedByUserFK: loginPinID,
      GeoLocationContactsJson: contactList?.map((item) => ({
        PhoneNo: item?.PhoneNo,
        FirstName: item?.FirstName,
        MiddleName: item?.MiddleName,
        LastName: item?.LastName,
        IsActive: 1,
        PhoneType: item?.PhoneType,
      })),
    };
    const response = await GeoServices.insertContactDetail(contactDetailPayload);
    if (response?.data?.success) {
      toastifySuccess("Data Saved Successfully");
      setIsContactChangeFields(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // ADD CONTACT ROW
  // ─────────────────────────────────────────────────────────────────────────
  const handleAddContactInformation = async () => {
    const lastNameError = !contactInformation.LastName?.trim() ? "Required" : "";
    handleErrorContactInformation("LastName", lastNameError);

    const hasPhoneType = contactInformation.PhoneType &&
      typeof contactInformation.PhoneType === "object" &&
      Object.keys(contactInformation.PhoneType).length > 0;

    let phoneNoError = "";
    if (hasPhoneType && !contactInformation.PhoneNo?.trim()) {
      phoneNoError = "Required";
    }
    handleErrorContactInformation("PhoneNo", phoneNoError);
    if (lastNameError || phoneNoError) return;
    if (!validateContactFormValues()) return;

    const { PhoneNo, FirstName, MiddleName, LastName, PhoneType } = contactInformation;
    if (!(FirstName || MiddleName || LastName || (PhoneNo && PhoneType?.label))) return;

    const payload = {
      PhoneNo,
      FirstName,
      MiddleName,
      LastName,
      PhoneType: PhoneType?.label,
      ID: editItemId || Date.now(),
    };

    setContactList((prev) =>
      isEditMode
        ? prev.map((c) => (c.ID === editItemId ? payload : c))
        : [...prev, payload]
    );
    setIsContactChangeFields(true);
    setContactInformation({ LastName: "", MiddleName: "", FirstName: "", PhoneType: {}, PhoneNo: "" });
    setIsEditMode(false);
    setEditItemId(null);
    setIsChangeFields(true);
    clearStateContactInformation();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // ★ GEO REVAMP — DOCUMENT UPLOAD
  // Uploads an attached document (SOP, floor plan, etc.) for this location.
  // Uses WhiteBoard-like upload flow:
  // /CAD/GeoDocument/InsertGeoDocuments with multipart FormData + Data payload.
  // ─────────────────────────────────────────────────────────────────────────
  const handleDocumentUpload = async () => {
    // if (!docUploadState.documentType) {
    //   toastifyError("Please select a document type");
    //   return;
    // }
    if (!docUploadState.file && !docUploadState.documentName) {
      toastifyError("Please select a file or enter a document name");
      return;
    }
    setIsUploadingDoc(true);
    try {
      const formData = new FormData();
      const encFormData = new FormData();

      if (docUploadState.file) {
        formData.append("File", docUploadState.file);
        encFormData.append("File", docUploadState.file);
      }

      const payload = {
        GeoID: geoLocationID,
        AgencyID: loginAgencyID,
        // DocumentType: docUploadState.documentType?.value || "",
        // DocumentName: docUploadState.documentName || docUploadState.file?.name || "",
        // Notes: docUploadState.notes || "",
        CreatedByUserFK: loginPinID,
      };

      // Keep payload format aligned with WhiteBoard upload structure.
      const valuesArray = [`${JSON.stringify(payload)}`];
      const formattedValues = JSON.stringify(valuesArray);
      formData.append("Data", formattedValues);
      encFormData.append("Data", formattedValues);

      const response = await AddDelete_Img('/CAD/GeoDocument/InsertGeoDocuments', formData, encFormData);
      if (response?.success || response?.data?.success) {
        toastifySuccess("Document uploaded successfully");
        setDocUploadState({ documentType: null, documentName: "", notes: "", file: null });
        if (docFileInputRef.current) docFileInputRef.current.value = "";
        loadDocuments();
      }
    } catch (e) {
      console.error("Document upload error:", e);
      toastifyError("Upload failed — check console for details");
    }
    setIsUploadingDoc(false);
  };

  const handleDocumentDelete = async (docID) => {
    try {
      const response = await GeoServices.deleteGeoDocument({
        DocumentID: docID,
        GeoLocationID: geoLocationID,
        AgencyID: loginAgencyID,
        DeletedByUserFK: loginPinID,
      });
      if (response?.data?.success) {
        toastifySuccess("Document removed");
        loadDocuments();
      }
    } catch (e) {
      console.error("Document delete error:", e);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CLEAR / CLOSE
  // ─────────────────────────────────────────────────────────────────────────
  const handleClear = () => {
    setGeoLocationID();
    clearState();
    setGEOFormValues(buildInitialFormValues());
    setPremiseSubTab("building");
    setContactInformation(initialValueContact);
    setIsEditMode(false);
    setIsChangeFields(false);
    clearStateContactInformation();
    setAptData({});
    setDocumentList([]);
    setHasSopDocuments(false);
  };

  const onCloseLocation = () => {
    setOpenGEOModal(false);
    clearState();
    setSelectedButton((prev) =>
      prev?.includes(4) ? prev?.filter((i) => i !== 4) : [...prev, 4]
    );
    setGeoLocationID("");
    setContactList([]);
    setIsSelectLocation(false);
    setContactInformation(initialValueContact);
    setIsChangeFields(false);
    setIsEditMode(false);
    clearStateContactInformation();
    setAptData({});
    setDocumentList([]);
    setHasSopDocuments(false);
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    if (confirmAction === "close" || confirmAction === "cancel") onCloseLocation();
    else if (confirmAction === "clear") handleClear();
  };

  const escFunction = useCallback((event) => {
    if (event.key === "Escape") onCloseLocation();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => document.removeEventListener("keydown", escFunction, false);
  }, [escFunction]);

  // ─────────────────────────────────────────────────────────────────────────
  // CUSTOM STYLES (preserved from original)
  // ─────────────────────────────────────────────────────────────────────────
  const customStylesWithFixedHeight = {
    ...customStylesWithOutColorNew,
    menu: (p) => ({ ...p, maxHeight: "100px" }),
    menuList: (p) => ({ ...p, maxHeight: "100px" }),
  };

  // ─────────────────────────────────────────────────────────────────────────
  // TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────
  const flagHistoryColumn = [
    {
      name: "Flag Name",
      selector: (row) => row.FlagNameCode || "",
      sortable: true,
      sortFunction: (a, b) => compareStrings(a.FlagNameCode, b.FlagNameCode),
      cell: (row) => <Tooltip text={row?.FlagNameCode || ""} maxLength={20} />,
      width: "150px",
    },
    {
      name: "Start Date",
      selector: (row) => row.FlagDateFrom ? getShowingWithOutTime(row.FlagDateFrom) : "",
      sortable: true,
      width: "160px",
    },
    {
      name: "Start Flag Details",
      selector: (row) => row.StartNote || "",
      cell: (row) => <Tooltip text={row?.StartNote || ""} maxLength={45} />,
      width: "380px",
    },
    {
      name: "End Date",
      selector: (row) => row.FlagDateTo ? getShowingDateText(row.FlagDateTo) : "",
      sortable: true,
      width: "160px",
    },
    {
      name: "End Flag Details",
      selector: (row) => row.EndNote || "",
      cell: (row) => <Tooltip text={row?.EndNote || ""} maxLength={45} />,
      width: "380px",
    },
  ];

  // ★ GEO REVAMP: Document list table columns
  const documentColumns = [
    {
      name: "Type",
      selector: (row) => GEO_DOC_TYPES.find((t) => t.value === row.DocumentType)?.label || row.DocumentType,
      width: "180px",
      style: { position: "static" },
    },
    {
      name: "Document Name",
      selector: (row) => row.DocumentName || "—",
      sortable: true,
      style: { position: "static" },
    },
    {
      name: "Notes",
      selector: (row) => row.Notes || "—",
      width: "220px",
      style: { position: "static" },
    },
    {
      name: "Uploaded",
      selector: (row) => row.CreatedDtTm ? getShowingWithOutTime(row.CreatedDtTm) : "—",
      width: "120px",
      style: { position: "static" },
    },
    {
      name: "Download",
      width: "90px",
      cell: (row) =>
        row.FileUrl ? (
          <a
            href={row.FileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-CADprimary py-0 px-2"
            title="Download document"
          >
            <FontAwesomeIcon icon={faDownload} />
          </a>
        ) : (
          <span style={{ color: "#aaa", fontSize: "12px" }}>No file</span>
        ),
      style: { position: "static" },
    },
    {
      name: "Remove",
      width: "80px",
      cell: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-danger py-0 px-2"
          title="Remove document"
          onClick={() => handleDocumentDelete(row.DocumentID)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      ),
      style: { position: "static" },
    },
  ];

  const pa = geoFormValues.premiseAttr || initialPremiseAttr;

  const renderPremiseYn = (value, onYes, onNo, { yesGreen = false } = {}) => (
    <div className="d-inline-flex border rounded overflow-hidden" style={{ height: 30 }}>
      <button
        type="button"
        className="border-0 px-3"
        style={{
          fontSize: 12,
          fontWeight: 500,
          background: value === true ? (yesGreen ? "#16a34a" : "#0d1b2e") : "#fff",
          color: value === true ? "#fff" : "#6b7280",
        }}
        onClick={onYes}
      >
        Yes
      </button>
      <button
        type="button"
        className="border-0 px-3"
        style={{
          fontSize: 12,
          fontWeight: 500,
          borderLeft: "1px solid #d1d5db",
          background: value === false ? "#6b7280" : "#fff",
          color: value === false ? "#fff" : "#6b7280",
        }}
        onClick={onNo}
      >
        No
      </button>
    </div>
  );

  /** Textareas: same auto-resize behavior as ClearModal / project standard */
  const onPremiseTextareaChange = (e, patch) => {
    updatePremiseAttr(patch);
    e.target.style.height = "auto";
    const maxHeight = 3 * parseFloat(getComputedStyle(e.target).lineHeight);
    e.target.style.height = `${Math.min(e.target.scrollHeight, maxHeight)}px`;
  };

  /** Radio marker: hollow when empty; filled when tab has data (partial = gray, complete = green) */
  const getPremiseTabRadioMarkerStyle = (kind) => {
    if (kind === "complete") {
      return {
        width: 8,
        height: 8,
        borderRadius: "50%",
        flexShrink: 0,
        border: "1px solid #15803d",
        background: "#16a34a",
        boxSizing: "border-box",
      };
    }
    if (kind === "partial") {
      return {
        width: 8,
        height: 8,
        borderRadius: "50%",
        flexShrink: 0,
        border: "1px solid #94a3b8",
        background: "#9ca3af",
        boxSizing: "border-box",
      };
    }
    return {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
      border: "1px solid #cbd5e1",
      background: "transparent",
      boxSizing: "border-box",
    };
  };

  const premiseTabBtn = (id, dotKey, label, icon) => {
    const active = premiseSubTab === id;
    const fillKind = premiseTabDots[dotKey] || "";
    return (
      <button
        key={id}
        type="button"
        className="d-flex align-items-center border-0 text-nowrap"
        style={{
          gap: 8,
          padding: "10px 16px 8px",
          minHeight: 44,
          fontSize: 13,
          fontWeight: active ? 700 : 500,
          color: active ? "#0f172a" : "#64748b",
          borderBottom: active ? "3px solid #000" : "3px solid transparent",
          background: active ? "#fff" : "transparent",
          fontFamily: "inherit",
          cursor: "pointer",
          alignSelf: "stretch",
          outline: "none",
          boxShadow: "none",
        }}
        onClick={() => setPremiseSubTab(id)}
      >
        <span style={getPremiseTabRadioMarkerStyle(fillKind)} aria-hidden />
        <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>
        <span>{label}</span>
      </button>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {openGEOModal ? (
        <dialog
          className="modal fade modal-cad"
          style={{ background: "rgba(0,0,0, 0.5)", zIndex: "200", overflowY: "hidden" }}
          id="GEOModal"
          tabIndex="-1"
          aria-hidden="true"
          data-backdrop="false"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div
              className="modal-content modal-content-cad"
              style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}
            >
              <div className="modal-body">

                {/* ── Modal title ───────────────────────────────────────────── */}
                <div className="row">
                  <div className="col-12">
                    <div className="py-0 px-2 d-flex justify-content-between align-items-center">
                      <p className="p-0 m-0" style={{ fontSize: 18, fontWeight: 500, letterSpacing: 0.5 }}>
                        GEO
                      </p>
                    </div>
                  </div>
                </div>

                {/* ── Tab navigation ────────────────────────────────────────── */}
                <div className="px-1">
                  <div className="col-12 name-tab m-0">
                    <ul className="nav nav-tabs mx-1">

                      {/* Home tab */}
                      <span
                        className={`nav-item ${showPage === TABS.HOME ? "active" : ""}`}
                        style={{ color: showPage === TABS.HOME ? "Red" : "#000" }}
                        onClick={() => setShowPage(TABS.HOME)}
                      >
                        <i className="fa fa-home" style={{ fontSize: "20px" }} />
                      </span>

                      {/* Contact Info tab ★ GEO REVAMP: no longer requires apt selection */}
                      <span
                        className={`nav-item ${showPage === TABS.CONTACT ? "active" : ""} ${!geoLocationID ? "disabled" : ""}`}
                        style={{
                          color: showPage === TABS.CONTACT && geoLocationID ? "Red" : "#000",
                          cursor: geoLocationID ? "pointer" : "not-allowed",
                        }}
                        onClick={() => { if (geoLocationID) setShowPage(TABS.CONTACT); }}
                      >
                        Contact Info
                      </span>

                      {/* ★ GEO REVAMP: Documents / SOP tab — NEW */}
                      <span
                        className={`nav-item ${showPage === TABS.DOCUMENTS ? "active" : ""} ${!geoLocationID ? "disabled" : ""}`}
                        style={{
                          color: showPage === TABS.DOCUMENTS && geoLocationID ? "Red" : "#000",
                          cursor: geoLocationID ? "pointer" : "not-allowed",
                          position: "relative",
                        }}
                        onClick={() => { if (geoLocationID) setShowPage(TABS.DOCUMENTS); }}
                      >
                        Documents / SOP
                        {/* SOP badge — shown when location has SOP documents */}
                        {hasSopDocuments && (
                          <span
                            style={{
                              background: "#001f3f",
                              color: "#fff",
                              borderRadius: "3px",
                              fontSize: "9px",
                              padding: "1px 5px",
                              marginLeft: "5px",
                              fontWeight: 700,
                            }}
                          >
                            SOP
                          </span>
                        )}
                      </span>

                      {/* Flag History tab */}
                      <span
                        className={`nav-item border-0 ${showPage === TABS.FLAG_HISTORY ? "active" : ""} ${!geoLocationID ? "disabled" : ""}`}
                        style={{
                          color: showPage === TABS.FLAG_HISTORY && geoLocationID ? "Red" : "#000",
                          cursor: geoLocationID ? "pointer" : "not-allowed",
                        }}
                        onClick={() => { if (geoLocationID) setShowPage(TABS.FLAG_HISTORY); }}
                      >
                        Flag History
                      </span>

                    </ul>
                  </div>
                </div>

                {/* ════════════════════════════════════════════════════════════
                    HOME TAB
                    ════════════════════════════════════════════════════════════ */}
                {showPage === TABS.HOME && (
                  <>
                    <div className="m-1">
                      <fieldset style={{ border: "1px solid gray" }}>
                        <div className="d-flex bb">

                          {/* Left: address form fields */}
                          <div className="col-9" style={{ display: "grid", gap: "5px" }}>
                            <div className="tab-form-container">

                              {/* Line 1: Location + Verified badge + Apt/Suite */}
                              <div className="tab-form-row">
                                <div className="col-8 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label pr-2" style={{ width: "17.6%", textAlign: "end" }}>
                                    Location
                                    {errorGeo.location && !geoFormValues.location && (
                                      <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>Required *</p>
                                    )}
                                  </label>
                                  <div className="w-100 d-flex inner-input-fullw tab-form-row-gap">
                                    <div style={{ flexGrow: 1 }}>
                                      <Location
                                        {...{
                                          value: geoFormValues,
                                          setValue: setGEOFormValues,
                                          locationStatus,
                                          setLocationStatus,
                                          updateStatus,
                                          setOnSelectLocation,
                                          locationList,
                                          setContactList,
                                          setGeoLocationID,
                                          flagDropDown,
                                          premiseDropDown,
                                          jurisdictionDropDown,
                                          setZoom,
                                          setIsSelectLocation,
                                          setIsChangeFields,
                                          // ★ GEO REVAMP: pass Patrol zone dropdown to Location
                                          // so it can auto-populate when a known address is selected
                                          geoZoneDropDown: [],
                                          locationData,
                                          setIsEditMode,
                                          setIsGoogleLocation,
                                          patrolZoneDropDown,
                                          fireZoneDropDown,
                                          emsZoneDropDown,
                                          mutualAidZoneDropDown,
                                          towZoneDropDown,
                                          otherZoneDropDown,
                                        }}
                                        col="location"
                                        isGEO
                                        locationID="NameLocationID"
                                        check={true}
                                        page="Name"
                                      />
                                    </div>
                                    <div className="d-flex align-items-center" style={{ marginLeft: "auto", gap: "8px" }}>
                                      {geoFormValues.location && (
                                        geoFormValues?.IsVerify ? (
                                          <span className="badge text-white" style={{ backgroundColor: "#008000", padding: "9px" }}>Verified</span>
                                        ) : (
                                          <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                              <BootstrapTooltip style={{ zIndex: 9999 }}>
                                                Please verify by entering coordinates and zones.
                                              </BootstrapTooltip>
                                            }
                                          >
                                            <span className="badge text-white p-2" style={{ textDecoration: "underline", backgroundColor: "#ff0000", cursor: "pointer" }}>
                                              Unverified
                                            </span>
                                          </OverlayTrigger>
                                        )
                                      )}
                                      {/* ★ GEO REVAMP: SOP badge on home tab */}
                                      {hasSopDocuments && (
                                        <span
                                          className="badge text-white"
                                          style={{ backgroundColor: "#001f3f", padding: "9px", cursor: "pointer" }}
                                          title="SOP documents on file — click Documents tab"
                                          onClick={() => geoLocationID && setShowPage(TABS.DOCUMENTS)}
                                        >
                                          📋 SOP
                                        </span>
                                      )}
                                      <span
                                        className="badge text-white"
                                        data-toggle="modal"
                                        data-target="#openAdvancedSearchModal"
                                        onClick={() => { setOpenAdvancedSearch(true); setAdvSearched(false); }}
                                        style={{ backgroundColor: "#008000", padding: "9px" }}
                                      >
                                        Advanced Search
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-3 d-flex align-self-center">
                                  <div className="col-4 d-flex align-self-center justify-content-end text-right">
                                    <label className="tab-form-label">Apt/Suite #</label>
                                  </div>
                                  <div className="w-100 d-flex align-self-center justify-content-end">
                                    <CreatableSelect
                                      isClearable
                                      options={aptSuiteNoDropDown.filter((o) => o.value && o.label)}
                                      placeholder="Select..."
                                      name="ApartmentNo"
                                      value={
                                        geoFormValues.ApartmentNo
                                          ? { value: geoFormValues.ApartmentNo, label: geoFormValues.ApartmentNo }
                                          : defaultOption || null
                                      }
                                      onChange={handleSelectAptSuitNo}
                                      onCreateOption={handleCreateOption}
                                      inputValue={aptInputValue}
                                      onInputChange={handleAptInputChange}
                                      styles={{ control: (p) => ({ ...p, width: "100%" }) }}
                                      className="w-100"
                                      menuPlacement="bottom"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Line 2: Premise # | St.Direction | St.Name | St.Direction */}
                              <div className="tab-form-row">
                                <div className="col-3 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label pr-2 d-flex justify-content-end text-nowrap" style={{ width: "85%" }}>Premise #</label>
                                  <div className="w-100 inner-input-fullw">
                                    <input
                                      type="number"
                                      className="form-control py-1 new-input"
                                      name="PremiseNo"
                                      disabled={geoFormValues?.isPremiseNo}
                                      value={geoFormValues.PremiseNo}
                                      onChange={(e) => {
                                        if (/^\d{0,4}$/.test(e.target.value)) handleInputChange(e);
                                      }}
                                      min="0"
                                    />
                                  </div>
                                </div>
                                <div className="col-2 d-flex align-self-center">
                                  <label className="tab-form-label col-4 d-flex align-self-center justify-content-end ml-4">St.Direction</label>
                                  <input type="text" className="form-control py-1 new-input" name="stDirection"
                                    maxLength="4" disabled={geoFormValues?.isStDirection}
                                    onKeyDown={handleKeyDown} value={geoFormValues.stDirection} onChange={handleInputChange} />
                                </div>
                                <div className="col-5 d-flex align-self-center">
                                  <div className="col-2 d-flex align-self-center justify-content-end">
                                    <label className="tab-form-label">St.Name</label>
                                  </div>
                                  <div className="col-10 d-flex align-self-center justify-content-end">
                                    <input type="text" className="form-control py-1 new-input" name="Street"
                                      maxLength={40} onKeyDown={handleNumberTextKeyDown} value={geoFormValues.Street}
                                      disabled={geoFormValues?.isStreet} onChange={handleInputChange} />
                                  </div>
                                </div>
                                <div className="col-2 d-flex align-self-center">
                                  <label className="tab-form-label col-4 d-flex align-self-center justify-content-end ml-4">St.Direction</label>
                                  <input type="text" className="form-control py-1 new-input" name="stDirection2"
                                    maxLength="4" onKeyDown={handleKeyDown} value={geoFormValues.stDirection2}
                                    disabled={geoFormValues?.isStDirection2} onChange={handleInputChange} />
                                </div>
                              </div>

                              {/* Line 3: Intersection St/St | Common Place Name */}
                              <div className="tab-form-row">
                                <div className="col-5 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap pr-2">Intersection St/St</label>
                                  <input type="text" className="form-control py-1 new-input" name="intersection1"
                                    value={geoFormValues.intersection1} disabled={geoFormValues?.isIntersection1}
                                    onKeyDown={handleNumberTextKeyDown} onChange={handleInputChange} />
                                  {"/"}
                                  <input type="text" className="form-control ml-1 py-1 new-input mr-2" name="intersection2"
                                    value={geoFormValues.intersection2} onKeyDown={handleNumberTextKeyDown}
                                    disabled={geoFormValues?.isIntersection2} onChange={handleInputChange} />
                                </div>
                                <div className="col-7 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap mr-1">Common Place Name</label>
                                  <input type="text" className="form-control py-1 new-input" name="commonPlaceName"
                                    value={geoFormValues.commonPlaceName} disabled={geoFormValues?.isCommonPlaceName}
                                    onChange={handleInputChange} />
                                </div>
                              </div>

                              {/* Line 4: Alt.St.Name | City | Zip */}
                              <div className="tab-form-row">
                                <div className="col-6 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label pr-2 d-flex justify-content-end" style={{ width: "28.5%" }}>Alt.St.Name</label>
                                  <input type="text" className="form-control py-1 new-input ml-1" name="AltStreet"
                                    onKeyDown={handleSpecialKeyDown} value={geoFormValues.AltStreet}
                                    disabled={geoFormValues?.isAltStreet} onChange={handleInputChange} />
                                </div>
                                <div className="col-4 d-flex align-items-center justify-content-end mr-2">
                                  <label className="tab-form-label mr-2">City</label>
                                  <input type="text" className="form-control py-1 new-input" name="City"
                                    value={geoFormValues.City} disabled={geoFormValues?.isCity}
                                    onKeyDown={handleKeyDown} onChange={handleInputChange} />
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-end" style={{ paddingRight: "15px" }}>
                                  <label className="tab-form-label mr-2">Zip</label>
                                  <input type="number" className="form-control py-1 new-input" name="ZipCode"
                                    value={geoFormValues.ZipCode} disabled={geoFormValues?.isZipCode} onChange={handleInputChange} />
                                </div>
                              </div>

                              {/* Line 5: Premise Type | Coordinate X/Y | Mile Marker */}
                              <div className="tab-form-row">
                                <div className="col-3 d-flex align-items-center justify-content-end mr-2">
                                  <label className="tab-form-label d-flex justify-content-end pr-2 text-nowrap" style={{ width: "60%" }}>Premise Type</label>
                                  <Select
                                    name="premiseType"
                                    styles={customStylesWithOutColorNew}
                                    isClearable
                                    options={premiseDropDown}
                                    placeholder="Select..."
                                    maxMenuHeight={130}
                                    className="w-100 ml-1"
                                    onChange={handleSelectChange}
                                    value={geoFormValues.premiseType || null}
                                    onInputChange={(v) => v.length > 12 ? v.slice(0, 12) : v}
                                  />
                                </div>

                                {/* ★ FIX: GC Modifier Select ──────────────────────────────────────────
                                    Optional field. When assigned, the RunCard engine will match this
                                    premise against elevated-response runcards (e.g. HIGHRISE card).
                                    Uses colourStylesNoReq (no amber required-field color) since optional.
                                    The stored value is gcModifier.code (the code string), not the label. */}
                                <div className="col-3 d-flex align-items-center justify-content-end mr-2">
                                  <label className="tab-form-label d-flex justify-content-end pr-2 text-nowrap" style={{ width: "50%" }}>
                                    GC Modifier
                                  </label>
                                  <Select
                                    name="gcModifier"
                                    styles={customStylesWithOutColorNew}
                                    isClearable
                                    options={gcModifierDropDown}
                                    placeholder="None..."
                                    maxMenuHeight={130}
                                    className="w-100 ml-1"
                                    onChange={handleSelectChange}
                                    value={geoFormValues.gcModifier || null}
                                    title="Assign a GC Modifier to trigger elevated apparatus response for this premise"
                                  />
                                </div>
                                <div className="col-4 d-flex align-self-center justify-content-end">
                                  <div className="d-flex align-self-center align-items-center justify-content-end">
                                    <label className="tab-form-label mr-1">
                                      Coordinate:X
                                      {errorGeo.coordinateX && !geoFormValues?.coordinateX && (
                                        <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>Enter Coordinate X</p>
                                      )}
                                    </label>
                                    <input type="number" className="form-control py-1 new-input" name="coordinateX"
                                      value={geoFormValues.coordinateX} disabled={geoFormValues?.isCoordinateX}
                                      onKeyDown={handleNumDotNoSpaceKeyDown} onChange={handleInputChange} />
                                  </div>
                                  <div className="d-flex align-self-center align-items-center justify-content-end">
                                    <label className="tab-form-label mx-2">
                                      Y
                                      {errorGeo.coordinateY && !geoFormValues?.coordinateY && (
                                        <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>Enter Coordinate Y</p>
                                      )}
                                    </label>
                                    <input type="number" className="form-control py-1 new-input" name="coordinateY"
                                      value={geoFormValues.coordinateY} disabled={geoFormValues?.isCoordinateY}
                                      onKeyDown={handleNumDotNoSpaceKeyDown} onChange={handleInputChange} />
                                  </div>
                                </div>
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label">Mile Marker</label>
                                </div>
                                <div className="col-1 d-flex align-items-center" style={{ paddingRight: "15px" }}>
                                  <input type="text" className="form-control py-1 new-input" name="mileMarker"
                                    value={geoFormValues.mileMarker} disabled={geoFormValues?.isMileMarker}
                                    onChange={handleInputChange} onKeyDown={handleNumberNoSpaceKeyDown} maxLength={4} />
                                </div>
                              </div>



                            </div>
                          </div>

                          {/* Right: Google Map panel */}
                          <div className="col-3">
                            {isMapVisible && isLoaded ? (
                              <GoogleMap
                                mapContainerStyle={{ height: "230px", width: "100%" }}
                                zoom={zoom}
                                center={mapCenter}
                                onLoad={handleMapLoad}
                                onZoomChanged={() => {
                                  if (mapRef.current) setZoom(mapRef.current.getZoom());
                                }}
                              >
                                {locations?.map((loc, idx) => (
                                  <Marker
                                    key={idx}
                                    position={{ lat: loc.lat, lng: loc.lng }}
                                    icon={{
                                      path: window.google.maps.SymbolPath.CIRCLE,
                                      scale: 10,
                                      fillColor: getColorByStatus(loc.status),
                                      fillOpacity: 1,
                                      strokeWeight: 1,
                                    }}
                                    title={loc.name}
                                  />
                                ))}
                              </GoogleMap>
                            ) : (
                              <div style={{ height: "230px", background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <span style={{ color: "#888", fontSize: "13px" }}>Loading Map...</span>
                              </div>
                            )}
                          </div>
                        </div>


                        {/* ★ Premise attributes — sub-tabs (Building / Fire / Hazmat / Occupancy / Access / PIP) */}
                        <div
                          className="w-100"
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 6,
                            // overflow: "hidden",
                            marginTop: 6,
                            marginBottom: 6,
                            background: "#fff",
                          }}
                        >
                          <div
                            className="d-flex"
                            role="tablist"
                            style={{
                              // overflowX: "auto",
                              background: "#f3f4f6",
                              // borderBottom: "1px solid #e5e7eb",
                              // alignItems: "stretch",
                            }}
                          >
                            {premiseTabBtn("building", "building", "Building", "🏢")}
                            {premiseTabBtn("protection", "protection", "Fire Protection", "💧")}
                            {premiseTabBtn("hazmat", "hazmat", "Hazmat", "☣")}
                            {premiseTabBtn("occupancy", "occupancy", "Occupancy", "👥")}
                            {premiseTabBtn("access", "access", "Access & Constraints", "🚪")}
                            {premiseTabBtn("pip", "pip", "PIP", "📋")}
                          </div>

                          {premiseSubTab === "building" && (
                            <>
                              <div style={{ padding: "14px 14px 10px" }}>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#374151",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    marginBottom: 10,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    paddingBottom: 6,
                                    borderBottom: "1px solid #f3f4f6",
                                  }}
                                >
                                  🏢 Building Attributes
                                  <span
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 700,
                                      background: "#e5e7eb",
                                      color: "#4b5563",
                                      padding: "1px 6px",
                                      borderRadius: 8,
                                      marginLeft: "auto",
                                      textTransform: "none",
                                      letterSpacing: 0,
                                    }}
                                  >
                                    premise
                                  </span>
                                </div>
                                {/* Row 1 */}
                                <div className="tab-form-row py-2">
                                  <div className="col-1 d-flex align-items-center justify-content-end">
                                    <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                      Structure Type
                                    </label>
                                  </div>
                                  <div className="col-3 d-flex align-items-center justify-content-end">
                                    <Select
                                      styles={colourStyles}
                                      isClearable
                                      options={structureTypesDropDown}
                                      placeholder="Select..."
                                      className="w-100"
                                      value={structureTypesDropDown.find((o) => o.value === pa.StructureTypeID) || null}
                                      maxMenuHeight={150}
                                      onChange={(opt) => updatePremiseAttr({ StructureTypeID: opt?.value ?? null })}
                                      onInputChange={(v) => (v.length > 12 ? v.slice(0, 12) : v)}
                                    />
                                  </div>

                                  <div className="col-1 d-flex align-items-center justify-content-end">
                                    <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                      Construction
                                    </label>
                                  </div>
                                  <div className="col-3 d-flex align-items-center justify-content-end">
                                    <Select
                                      styles={colourStyles}
                                      isClearable
                                      options={constructionDropDown}
                                      placeholder="Select..."
                                      className="w-100"
                                      value={constructionDropDown.find((o) => o.value === pa.ConstructionTypeID) || null}
                                      maxMenuHeight={150}
                                      onChange={(opt) => updatePremiseAttr({ ConstructionTypeID: opt?.value ?? null })}
                                      onInputChange={(v) => (v.length > 12 ? v.slice(0, 12) : v)}
                                    />
                                  </div>


                                  <div className="col-2 d-flex align-items-center justify-content-end">
                                    <label className="tab-form-label text-nowrap" style={{ textAlign: "end", marginRight: "10px" }}>
                                      Stories
                                    </label>
                                    <input
                                      type="number"
                                      className="form-control new-input w-100"
                                      min={1}
                                      max={200}
                                      value={pa.Stories}
                                      onChange={(e) => updatePremiseAttr({ Stories: e.target.value })}
                                    />
                                  </div>




                                  <div className="col-2 d-flex align-items-center justify-content-end">
                                    <label className="tab-form-label text-nowrap" style={{ textAlign: "end", marginRight: "10px" }}>
                                      Year Built
                                    </label>
                                    <input
                                      type="number"
                                      className="form-control new-input w-100"
                                      min={1700}
                                      max={2100}
                                      placeholder="YYYY"
                                      value={pa.YearBuilt}
                                      onChange={(e) => updatePremiseAttr({ YearBuilt: e.target.value })}
                                    />
                                  </div>

                                </div>

                                {/* Row 2 */}
                                <div className="tab-form-row py-2" >
                                  <div className="col-1 d-flex align-items-center justify-content-end">
                                    <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                      Roof Notes

                                    </label>
                                  </div>
                                  <div className="col-11 d-flex align-items-center justify-content-end">
                                    <textarea
                                      name="premiseRoofNotes"
                                      className="form-control py-1 new-input"
                                      rows={3}
                                      placeholder="e.g., Solar panels on south side — no vertical ventilation."
                                      value={pa.RoofNotes}
                                      style={{ height: "auto", overflowY: "scroll", flex: 1, minWidth: 0 }}
                                      onChange={(e) => onPremiseTextareaChange(e, { RoofNotes: e.target.value })}
                                    />
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {premiseSubTab === "protection" && (
                            <div style={{ padding: "14px 14px 10px", background: "#fff" }}>

                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#374151",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  marginBottom: 10,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  paddingBottom: 6,
                                  borderBottom: "1px solid #f3f4f6",
                                }}
                              >
                                💧 Fire Protection
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    background: "#e5e7eb",
                                    color: "#4b5563",
                                    padding: "1px 6px",
                                    borderRadius: 8,
                                    marginLeft: "auto",
                                    textTransform: "none",
                                  }}
                                >
                                  premise
                                </span>
                              </div>
                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Sprinklers
                                  </label>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-start">
                                  {renderPremiseYn(
                                    pa.Sprinklers,
                                    () => updatePremiseAttr({ Sprinklers: true }),
                                    () => updatePremiseAttr({ Sprinklers: false, SprinklerTypeID: null }),
                                    { yesGreen: true }
                                  )}
                                </div>


                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Sprinkler Type
                                  </label>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-start">
                                  <Select
                                    styles={pa.Sprinklers ? colourStyles : customStylesWithOutColorNew}
                                    isClearable
                                    isDisabled={pa.Sprinklers !== true}
                                    options={sprinklerTypeDropDown}
                                    placeholder="Select..."
                                    className="w-100"
                                    value={sprinklerTypeDropDown.find((o) => o.value === pa.SprinklerTypeID) || null}
                                    maxMenuHeight={150}
                                    onChange={(opt) => updatePremiseAttr({ SprinklerTypeID: opt?.value ?? null })}
                                    onInputChange={(v) => (v.length > 12 ? v.slice(0, 12) : v)}
                                  />
                                </div>

                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Fire Alarm
                                  </label>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-start">
                                  {renderPremiseYn(
                                    pa.FireAlarmSystem,
                                    () => updatePremiseAttr({ FireAlarmSystem: true }),

                                    () => updatePremiseAttr({ FireAlarmSystem: false }),
                                    { yesGreen: true },
                                  )}
                                </div>

                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Knox Box
                                  </label>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-start">
                                  {renderPremiseYn(
                                    pa.KnoxBoxPresent,
                                    () => updatePremiseAttr({ KnoxBoxPresent: true }),
                                    () => updatePremiseAttr({ KnoxBoxPresent: false, KnoxBoxLocation: "" }),
                                    { yesGreen: true },
                                  )}
                                </div>

                              </div>
                              {/* Row 2 */}
                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Knox Location
                                  </label>
                                </div>
                                <div className="col-11 d-flex align-items-center justify-content-end">
                                  <input
                                    type="text"
                                    className="form-control py-1 new-input flex-grow-1"
                                    disabled={pa.KnoxBoxPresent !== true}
                                    placeholder="e.g., Front entrance, left of main door"
                                    value={pa.KnoxBoxLocation}
                                    onChange={(e) => updatePremiseAttr({ KnoxBoxLocation: e.target.value })}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {premiseSubTab === "hazmat" && (
                            <div style={{ padding: "14px 14px 10px", background: "#fff" }}>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#374151",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  marginBottom: 10,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  paddingBottom: 6,
                                  borderBottom: "1px solid #f3f4f6",
                                }}
                              >
                                ☣ Hazmat on Premise
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    background: "#e5e7eb",
                                    color: "#4b5563",
                                    padding: "1px 6px",
                                    borderRadius: 8,
                                    marginLeft: "auto",
                                    textTransform: "none",
                                  }}
                                >
                                  premise
                                </span>
                              </div>
                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    On File
                                  </label>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-start">
                                  {renderPremiseYn(
                                    pa.HazmatOnFile,
                                    () => updatePremiseAttr({ HazmatOnFile: true }),
                                    () => updatePremiseAttr({ HazmatOnFile: false, HazmatClassID: null, HazmatDescription: "" }),
                                    { yesGreen: true },
                                  )}
                                </div>

                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Class
                                  </label>
                                </div>
                                <div className="col-3 d-flex align-items-center justify-content-start">
                                  <Select
                                    styles={pa.HazmatOnFile ? colourStyles : customStylesWithOutColorNew}
                                    isClearable
                                    isDisabled={pa.HazmatOnFile !== true}
                                    options={hazmatClassDropDown}
                                    placeholder="Select..."
                                    className="w-100"
                                    value={hazmatClassDropDown.find((o) => o.value === pa.HazmatClassID) || null}
                                    maxMenuHeight={150}
                                    onChange={(opt) => updatePremiseAttr({ HazmatClassID: opt?.value ?? null })}
                                    onInputChange={(v) => (v.length > 12 ? v.slice(0, 12) : v)}
                                  />
                                </div>
                              </div>
                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Description
                                  </label>
                                </div>
                                <div className="col-11 d-flex align-items-center justify-content-end">
                                  <textarea
                                    name="premiseHazmatDescription"
                                    className="form-control py-1 new-input"
                                    rows={3}
                                    disabled={pa.HazmatOnFile !== true}
                                    placeholder="Substance name, typical quantities, on-premise location, placards..."
                                    value={pa.HazmatDescription}
                                    style={{ height: "auto", overflowY: "scroll", flex: 1, minWidth: 0 }}
                                    onChange={(e) => onPremiseTextareaChange(e, { HazmatDescription: e.target.value })}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {premiseSubTab === "occupancy" && (
                            <div style={{ padding: "14px 14px 10px", background: "#fff" }}>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#374151",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  marginBottom: 10,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  paddingBottom: 6,
                                  borderBottom: "1px solid #f3f4f6",
                                }}
                              >
                                👥 Occupancy Estimate
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    background: "#e5e7eb",
                                    color: "#4b5563",
                                    padding: "1px 6px",
                                    borderRadius: 8,
                                    marginLeft: "auto",
                                    textTransform: "none",
                                  }}
                                >
                                  premise
                                </span>
                              </div>

                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Day Est.
                                  </label>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-start">
                                  <input
                                    type="number"
                                    className="form-control py-1 new-input w-100"
                                    style={{ width: 70 }}
                                    min={0}
                                    value={pa.OccupancyDay}
                                    onChange={(e) => updatePremiseAttr({ OccupancyDay: e.target.value })}
                                  />
                                </div>
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Night Est.
                                  </label>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-start">
                                  <input
                                    type="number"
                                    className="form-control py-1 new-input w-100"
                                    style={{ width: 70 }}
                                    min={0}
                                    value={pa.OccupancyNight}
                                    onChange={(e) => updatePremiseAttr({ OccupancyNight: e.target.value })}
                                  />
                                </div>
                              </div>

                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Notes
                                  </label>
                                </div>
                                <div className="col-11 d-flex align-items-center justify-content-start">
                                  <input
                                    type="text"
                                    className="form-control py-1 new-input flex-grow-1 w-100"
                                    placeholder="e.g., Children K–6, mobility-impaired residents"
                                    value={pa.OccupancyNotes}
                                    onChange={(e) => updatePremiseAttr({ OccupancyNotes: e.target.value })}
                                  />
                                </div>

                              </div>




                            </div>
                          )}

                          {premiseSubTab === "access" && (
                            <div style={{ padding: "14px 14px 10px", background: "#fff" }}>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#374151",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  marginBottom: 10,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  paddingBottom: 6,
                                  borderBottom: "1px solid #f3f4f6",
                                }}
                              >
                                🚪 Access & Constraints
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    background: "#e5e7eb",
                                    color: "#4b5563",
                                    padding: "1px 6px",
                                    borderRadius: 8,
                                    marginLeft: "auto",
                                    textTransform: "none",
                                  }}
                                >
                                  premise
                                </span>
                              </div>
                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Hydrants
                                  </label>
                                </div>
                                <div className="col-11 d-flex align-items-center justify-content-end">
                                  <textarea
                                    name="premiseHydrantNotes"
                                    className="form-control py-1 new-input"
                                    rows={3}
                                    placeholder="e.g., Hydrant on north side at Oak St."
                                    value={pa.HydrantNotes}
                                    style={{ height: "auto", overflowY: "scroll", flex: 1, minWidth: 0 }}
                                    onChange={(e) => onPremiseTextareaChange(e, { HydrantNotes: e.target.value })}
                                  />
                                </div>
                              </div>
                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Access
                                  </label>
                                </div>
                                <div className="col-11 d-flex align-items-center justify-content-end">
                                  <textarea
                                    name="premiseAccessNotes"
                                    className="form-control py-1 new-input"
                                    rows={3}
                                    placeholder="e.g., Gate code, loading dock, after-hours entry"
                                    value={pa.AccessNotes}
                                    style={{ height: "auto", overflowY: "scroll", flex: 1, minWidth: 0 }}
                                    onChange={(e) => onPremiseTextareaChange(e, { AccessNotes: e.target.value })}
                                  />
                                </div>
                              </div>
                              <div v className="tab-form-row py-2" >
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Constraints
                                  </label>
                                </div>

                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                  <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                    {[
                                      ["ConstraintNarrowAccess", "🚧 Narrow Access"],
                                      ["ConstraintLowClearance", "📐 Low Clearance"],
                                      ["ConstraintWeightLimit", "⚖ Weight Limit"],
                                      ["ConstraintAerialRestricted", "🪜 Aerial Restricted"],
                                      ["ConstraintSteepGrade", "⛰ Steep Grade"],
                                    ].map(([key, lab]) => {
                                      const checked = !!pa[key];
                                      return (
                                        <label
                                          key={key}
                                          className="d-inline-flex align-items-center mb-0"
                                          style={{
                                            gap: 5,
                                            padding: "5px 10px",
                                            border: `1px solid ${checked ? "#fca5a5" : "#d1d5db"}`,
                                            borderRadius: 14,
                                            background: checked ? "#fef2f2" : "#fff",
                                            fontSize: 11,
                                            color: checked ? "#991b1b" : "#4b5563",
                                            fontWeight: checked ? 600 : 400,
                                            cursor: "pointer",
                                            userSelect: "none",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            className="m-0"
                                            style={{ width: 13, height: 13, accentColor: "#dc2626" }}
                                            checked={checked}
                                            onChange={(e) => updatePremiseAttr({ [key]: e.target.checked })}
                                          />
                                          <span>{lab}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                  <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4, fontStyle: "italic" }}>
                                    Affects apparatus dispatchability — e.g. ladder truck routing, weight-restricted approaches.
                                  </div>
                                </div>
                              </div>
                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Constraint Notes
                                  </label>
                                </div>
                                <div className="col-11 d-flex align-items-center justify-content-end">
                                  <input
                                    type="text"
                                    className="form-control py-1 new-input flex-grow-1"
                                    placeholder="e.g., 12-ft clearance under bridge"
                                    value={pa.ConstraintNotes}
                                    onChange={(e) => updatePremiseAttr({ ConstraintNotes: e.target.value })}
                                  />
                                </div>
                              </div>
                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Dispatcher Notes
                                  </label>
                                </div>
                                <div className="col-11 d-flex align-items-center justify-content-end">
                                  <input
                                    type="text"
                                    className="form-control py-1 new-input flex-grow-1"
                                    placeholder="One-line catch-all (e.g., Owner-occupied 24/7)"
                                    value={pa.DispatcherNotes}
                                    onChange={(e) => updatePremiseAttr({ DispatcherNotes: e.target.value })}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {premiseSubTab === "pip" && (
                            <div style={{ padding: "14px 14px 10px", background: "#fff" }}>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#374151",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  marginBottom: 10,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  paddingBottom: 6,
                                  borderBottom: "1px solid #f3f4f6",
                                }}
                              >
                                📋 Pre-Incident Plan Document
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    background: "#e5e7eb",
                                    color: "#4b5563",
                                    padding: "1px 6px",
                                    borderRadius: 8,
                                    marginLeft: "auto",
                                    textTransform: "none",
                                  }}
                                >
                                  premise
                                </span>
                              </div>



                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    PIP On File
                                  </label>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-start">
                                  {renderPremiseYn(
                                    pa.PIPOnFile,
                                    () => updatePremiseAttr({ PIPOnFile: true }),
                                    () => updatePremiseAttr({
                                      PIPOnFile: false,
                                      PIPVersion: "",
                                      PIPLastUpdated: "",
                                      PIPDocumentRef: "",
                                    }),
                                    { yesGreen: true },
                                  )}
                                </div>
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Version
                                  </label>
                                </div>
                                <div className="col-2 d-flex align-items-center justify-content-start">
                                  <input
                                    type="text"
                                    className="form-control py-1 new-input flex-grow-1"
                                    disabled={pa.PIPOnFile !== true}
                                    placeholder="v1.0"
                                    value={pa.PIPVersion}
                                    onChange={(e) => updatePremiseAttr({ PIPVersion: e.target.value })}
                                  />
                                </div>
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Last Updated
                                  </label>
                                </div>
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                    <DatePicker
                                      id="premisePipLastUpdated"
                                      name="premisePipLastUpdated"
                                      disabled={pa.PIPOnFile !== true}
                                      selected={parsePremisePipDate(pa.PIPLastUpdated)}
                                      onChange={(date) => updatePremiseAttr({
                                        PIPLastUpdated: date ? getShowingWithOutTime(date) : "",
                                      })}
                                      isClearable={!!pa.PIPLastUpdated}
                                      placeholderText={pa.PIPLastUpdated || "Select..."}
                                      dateFormat="MM/dd/yyyy"
                                      showMonthDropdown
                                      showYearDropdown
                                      dropdownMode="select"
                                      showDisabledMonthNavigation
                                      autoComplete="off"
                                      onKeyDown={(e) => {
                                        if (!((e.key >= "0" && e.key <= "9") || e.key === "Backspace" || e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Delete" || e.key === ":" || e.key === "/" || e.key === " " || e.key === "F5")) {
                                          e.preventDefault();
                                        }
                                      }}
                                      className="form-control py-1 new-input w-100"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="tab-form-row py-2">
                                <div className="col-1 d-flex align-items-center justify-content-end">
                                  <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                                    Document
                                  </label>
                                </div>
                                <div className="col-11 d-flex align-items-center justify-content-end">
                                  <input
                                    type="text"
                                    className="form-control py-1 new-input flex-grow-1"
                                    disabled={pa.PIPOnFile !== true}
                                    placeholder="Filename or reference (uploaded under Documents/SOP tab)"
                                    value={pa.PIPDocumentRef}
                                    onChange={(e) => updatePremiseAttr({ PIPDocumentRef: e.target.value })}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* ── Line 6: Jurisdiction (multi-select) ★ GEO REVAMP ────── */}
                        <div className="tab-form-row py-2">
                          <div className="col-1 d-flex align-items-center justify-content-end">
                            <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                              Jurisdiction
                              {errorGeo.jurisdiction && (!geoFormValues.jurisdiction || geoFormValues.jurisdiction.length === 0) && (
                                <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>Required *</p>
                              )}
                            </label>
                          </div>
                          {/* ★ GEO REVAMP: isMulti=true — addresses can have multiple jurisdictions.
                              Each option is colour-coded: green border = currently active,
                              grey = outside current hours of operation. */}
                          <div className="tab-form-row-gap d-flex ml-2" style={{ width: "40%" }}>
                            <Select
                              name="jurisdiction"
                              styles={colourStyles}
                              isClearable
                              isMulti
                              options={jurisdictionDropDown}
                              placeholder="Select jurisdiction(s)..."
                              className="w-100 ml-1"
                              value={Array.isArray(geoFormValues.jurisdiction) ? geoFormValues.jurisdiction : []}
                              getOptionLabel={(v) => `${v?.JurisdictionCode} | ${v?.Name}`}
                              getOptionValue={(v) => v?.JurisdictionCode}
                              // ★ GEO REVAMP: Format each option to show active/inactive status
                              formatOptionLabel={(option, { context }) => {
                                const isActive = isJurisdictionActiveNow(option);
                                if (context === "value") return `${option?.JurisdictionCode}${isActive ? " ✓" : " (off-hours)"}`;
                                return (
                                  <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                                    <span style={{
                                      width: "8px", height: "8px", borderRadius: "50%",
                                      background: isActive ? "#28a745" : "#6c757d",
                                      display: "inline-block", flexShrink: 0,
                                    }} />
                                    <span>{option?.JurisdictionCode} | {option?.Name}</span>
                                    {!isActive && (
                                      <span style={{ fontSize: "10px", color: "#6c757d", marginLeft: "auto" }}>
                                        off-hours
                                      </span>
                                    )}
                                    {isActive && option?.HoursOfOperationFrom && (
                                      <span style={{ fontSize: "10px", color: "#28a745", marginLeft: "auto" }}>
                                        active now
                                      </span>
                                    )}
                                  </div>
                                );
                              }}
                              maxMenuHeight={130}
                              onChange={(selected) => {
                                setGEOFormValues((prev) => ({ ...prev, jurisdiction: selected || [] }));
                                setIsChangeFields(true);
                              }}
                            />
                          </div>
                        </div>

                        {/* ── Line 7: Zone row ★ GEO REVAMP ─────────────────────── */}
                        {/* Each dropdown now shows only the correct zone type.
                            Law Zone → Patrol, Fire Zone → Fire/ESN, etc. */}
                        <div className="tab-form-row py-2">
                          <div className="col-1 d-flex align-items-center justify-content-end">
                            <label className="tab-form-label text-nowrap">
                              Law Zone
                              {errorGeo.patrolZone && isEmptyObject(geoFormValues.patrolZone) && (
                                <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>Required *</p>
                              )}
                            </label>
                          </div>
                          <div className="col-11 tab-form-row-gap d-flex w-100">

                            {/* Law / Patrol Zone */}
                            <Select
                              name="patrolZone"
                              styles={colourStyles}
                              isClearable
                              options={patrolZoneDropDown}   // ★ Patrol only
                              placeholder="Select..."
                              className="w-100 ml-1"
                              value={geoFormValues.patrolZone || null}
                              maxMenuHeight={100}
                              onChange={handleSelectChange}
                              onInputChange={(v) => v.length > 12 ? v.slice(0, 12) : v}
                            />

                            {/* ★ GEO REVAMP: Beat Number field immediately after Law Zone */}
                            <div className="d-flex align-items-center" style={{ gap: "6px", flexShrink: 0 }}>
                              <label className="tab-form-label text-nowrap">Beat #</label>
                              <input
                                type="text"
                                className="form-control py-1 new-input"
                                name="beatNumber"
                                placeholder="e.g. 4A"
                                maxLength={10}
                                value={geoFormValues.beatNumber || ""}
                                onChange={handleInputChange}
                                style={{ width: "70px" }}
                                title="Law enforcement beat number within the patrol zone"
                              />
                            </div>

                            {/* Fire Zone */}
                            <div className="d-flex align-items-center justify-content-end">
                              <label className="tab-form-label text-nowrap mr-1">
                                Fire Zone
                                {errorGeo.fireZone && isEmptyObject(geoFormValues.fireZone) && (
                                  <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>Required *</p>
                                )}
                              </label>
                            </div>
                            <Select
                              name="fireZone"
                              styles={colourStyles}
                              isClearable
                              options={fireZoneDropDown}    // ★ Fire/ESN only
                              placeholder="Select ESN..."
                              className="w-100"
                              value={geoFormValues.fireZone || null}
                              maxMenuHeight={100}
                              onChange={handleSelectChange}
                              onInputChange={(v) => v.length > 12 ? v.slice(0, 12) : v}
                            />

                            {/* EMS Zone */}
                            <div className="d-flex align-items-center justify-content-end">
                              <label className="tab-form-label text-nowrap mr-1">
                                EMS Zone
                                {errorGeo.emsZone && isEmptyObject(geoFormValues.emsZone) && (
                                  <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>Required *</p>
                                )}
                              </label>
                            </div>
                            <Select
                              name="emsZone"
                              styles={colourStyles}
                              isClearable
                              options={emsZoneDropDown}    // ★ EMS only
                              placeholder="Select..."
                              className="w-100"
                              value={geoFormValues.emsZone || null}
                              maxMenuHeight={100}
                              onChange={handleSelectChange}
                              onInputChange={(v) => v.length > 12 ? v.slice(0, 12) : v}
                            />
                            <div className="d-flex align-items-center justify-content-end">
                              <label className="tab-form-label text-nowrap mr-1">
                                Other Zone
                                {errorGeo.otherZone && isEmptyObject(geoFormValues.otherZone) && (
                                  <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>Required *</p>
                                )}
                              </label>
                            </div>
                            <Select
                              name="otherZone"
                              styles={colourStyles}
                              isClearable
                              options={otherZoneDropDown}      // ★ Other only
                              placeholder="Select..."
                              className="w-100"
                              value={geoFormValues.otherZone || null}
                              maxMenuHeight={100}
                              onChange={handleSelectChange}
                            />
                          </div>
                        </div>

                        {/* ── Line 8: Second zone row (Mutual Aid / Tow / Other) ★ GEO REVAMP ── */}
                        {/* <div className="tab-form-row py-1">
                          <div className="col-1 d-flex align-items-center justify-content-end">
                            <label className="tab-form-label text-nowrap" style={{ textAlign: "end" }}>
                              Mutual Aid
                            </label>
                          </div>
                          <div className="col-11 tab-form-row-gap d-flex w-100">

                            <Select
                              name="mutualAidZone"
                              styles={colourStylesNoReq}        // optional — no amber
                              isClearable
                              options={mutualAidZoneDropDown}   // ★ MutualAid only
                              placeholder="Select..."
                              className="w-100 ml-1"
                              value={geoFormValues.mutualAidZone || null}
                              maxMenuHeight={100}
                              onChange={handleSelectChange}
                            />

                            <div className="d-flex align-items-center justify-content-end">
                              <label className="tab-form-label text-nowrap mr-1">Tow Zone</label>
                            </div>
                            <Select
                              name="towZone"
                              styles={colourStylesNoReq}        // optional — no amber
                              isClearable
                              options={towZoneDropDown}         // ★ Tow only
                              placeholder="Select..."
                              className="w-100"
                              value={geoFormValues.towZone || null}
                              maxMenuHeight={100}
                              onChange={handleSelectChange}
                            />

                            <div className="d-flex align-items-center justify-content-end">
                              <label className="tab-form-label text-nowrap mr-1">
                                Other Zone
                                {errorGeo.otherZone && isEmptyObject(geoFormValues.otherZone) && (
                                  <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>Required *</p>
                                )}
                              </label>
                            </div>
                            <Select
                              name="otherZone"
                              styles={colourStyles}
                              isClearable
                              options={otherZoneDropDown}      // ★ Other only
                              placeholder="Select..."
                              className="w-100"
                              value={geoFormValues.otherZone || null}
                              maxMenuHeight={100}
                              onChange={handleSelectChange}
                            />

                          </div>
                        </div> */}

                        {/* ── Line 9: Flags row (unchanged from original) ──────────── */}
                        <div className="tab-form-row py-2">
                          <div className="col-1 d-flex align-items-center justify-content-end ml-1">
                            <label className="tab-form-label text-nowrap">Flags</label>
                          </div>
                          <div className="col-11 d-flex tab-form-row-gap" style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            {flagBudgeList?.Table?.map((item, index) => {
                              const buttons = [];
                              if (item?.Is24HourFlag === 1) {
                                buttons.push(
                                  <button
                                    key={`24Hour-${index}`}
                                    type="button"
                                    style={{
                                      backgroundColor: "#FF0000",
                                      color: "#ffff",
                                      border: "none",
                                      whiteSpace: "nowrap",
                                    }}
                                    data-toggle="modal"
                                    data-target="#FlagTableModal"
                                    onClick={() => { setOpenFlagTableModal(true); setFlagName("Is24HourFlag"); }}
                                    className="btn btn-sm btn-CADprimary1"
                                  >
                                    24 Hr
                                  </button>
                                );
                              }
                              if (item?.PremiseFlag === 1) {
                                buttons.push(

                                  <button
                                    key={`Premise-${index}`}
                                    type="button"
                                    style={{
                                      backgroundColor: "#28a745",
                                      color: "#ffff",
                                      border: "none",
                                      whiteSpace: "nowrap",
                                    }}
                                    data-toggle="modal"
                                    data-target="#FlagTableModal"
                                    onClick={() => { setOpenFlagTableModal(true); setFlagName("PremiseFlag") }}
                                    className="btn btn-sm btn-CADprimary1"
                                  >
                                    Premise History
                                  </button>
                                );
                              }
                              return <React.Fragment key={`item-${index}`}>{buttons}</React.Fragment>;
                            })}
                            {flagBudgeList?.Table1?.map((item, index) => (
                              <button
                                key={index}
                                type="button"
                                style={{
                                  backgroundColor: item?.PriorityColor || "#e6e6e6",
                                  color: "#ffff",
                                  border: "none",
                                  whiteSpace: "nowrap",
                                }}
                                data-toggle="modal"
                                data-target="#addFlagModal"
                                onClick={() => { setOpenAddFlagModal(true); setSelectedFlagData(item); }}
                                className="btn btn-sm btn-CADprimary1"
                              >
                                {item?.FlagType}
                              </button>
                            ))}
                            <div className="d-flex align-items-center" style={{ whiteSpace: "nowrap" }}>
                              <span
                                data-toggle={isSelectLocation ? "modal" : undefined}
                                data-target={isSelectLocation ? "#addFlagModal" : undefined}
                                onClick={
                                  isSelectLocation
                                    ? () => {
                                      setOpenAddFlagModal(true);
                                      setSelectedFlagData({});
                                    }
                                    : undefined
                                }
                                className={`pt-1 ${!isSelectLocation ? "disabled" : ""}`}
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "700",
                                  cursor: isSelectLocation ? "pointer" : "not-allowed",
                                  color: isSelectLocation ? "blue" : "gray",
                                }}
                              >
                                Add Flag
                              </span>
                            </div>
                          </div>
                        </div>

                      </fieldset>
                    </div>

                    {/* Home tab action buttons */}
                    <div className="row">
                      <div className="col-12 p-0">
                        <div className="py-0 px-2 d-flex justify-content-end align-items-center">
                          <div className="d-flex justify-content-end tab-form-row-gap mt-1">
                            <button type="button" className="save-button ml-2"
                              disabled={!isChangeFields || disabled} onClick={handleSave}>
                              {geoFormValues?.isUpdate ? "Update" : "Save"}
                            </button>
                            <button type="button" className="cancel-button"
                              onClick={() => {
                                if (isChangeFields) { setConfirmAction("cancel"); setShowConfirmModal(true); }
                                else onCloseLocation();
                              }}>
                              Cancel
                            </button>
                            <button type="button" className="cancel-button"
                              onClick={() => {
                                if (isChangeFields) { setConfirmAction("clear"); setShowConfirmModal(true); }
                                else handleClear();
                              }}>
                              Clear
                            </button>
                            <button type="button" className="cancel-button"
                              onClick={() => {
                                if (isChangeFields) { setConfirmAction("close"); setShowConfirmModal(true); }
                                else onCloseLocation();
                              }}>
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ════════════════════════════════════════════════════════════
                    CONTACT INFO TAB
                    ★ GEO REVAMP: no longer gated behind apt selection.
                    Works at GeoLocationID level when no apt selected.
                    ════════════════════════════════════════════════════════════ */}
                {showPage === TABS.CONTACT && (
                  <>
                    <div className="m-1">
                      <fieldset style={{ border: "1px solid gray" }}>

                        {/* Name row */}
                        <div className="tab-form-row">
                          <div className="col-1 d-flex align-items-center justify-content-end ml-1">
                            <label className="tab-form-label text-nowrap">
                              Lastname
                              {errorContactInformation.LastName && (
                                <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>{errorContactInformation.LastName}</p>
                              )}
                            </label>
                          </div>
                          <div className="col-11 tab-form-row-gap">
                            <input type="text"
                              className={ClassNames("form-control py-1 new-input requiredColor")}
                              name="LastName" required value={contactInformation.LastName}
                              onKeyDown={handleKeyDown} onChange={handleContactInputChange} />
                            <div className="d-flex align-self-center justify-content-end">
                              <label className="tab-form-label text-nowrap">Middlename</label>
                            </div>
                            <input type="text" className="form-control py-1 new-input"
                              onKeyDown={handleKeyDown} name="MiddleName"
                              value={contactInformation.MiddleName} onChange={handleContactInputChange} />
                            <div className="d-flex align-self-center justify-content-end">
                              <label className="tab-form-label text-nowrap">Firstname</label>
                            </div>
                            <input type="text" className="form-control py-1 new-input"
                              name="FirstName" value={contactInformation.FirstName}
                              onKeyDown={handleKeyDown} onChange={handleContactInputChange} />
                          </div>
                        </div>

                        {/* Phone row */}
                        <div className="tab-form-row py-1">
                          <div className="d-flex align-items-center justify-content-end">
                            <label className="tab-form-label text-nowrap" style={{ marginLeft: "40px" }}>Phone Type</label>
                          </div>
                          <div className="col-2 tab-form-row-gap d-flex w-100" style={{ marginLeft: "2px" }}>
                            <Select
                              name="PhoneType"
                              styles={customStylesWithFixedHeight}
                              options={phoneTypes}
                              placeholder="Select..."
                              className="w-100 ml-1"
                              isClearable
                              value={contactInformation.PhoneType || null}
                              onChange={handleSelectPhoneType}
                              onInputChange={(v) => v.length > 12 ? v.slice(0, 12) : v}
                            />
                          </div>
                          <div className="col-4 d-flex align-self-center" style={{ marginLeft: "38px" }}>
                            <div className="col-4 d-flex align-self-center justify-content-end">
                              <label className="tab-form-label">
                                Phone Number
                                {errorContactInformation.PhoneNo && (
                                  <p style={{ color: "red", fontSize: "11px", margin: "0px", padding: "0px" }}>{errorContactInformation.PhoneNo}</p>
                                )}
                              </label>
                            </div>
                            <input type="text"
                              className={ClassNames("form-control py-1 new-input", {
                                requiredColor: contactInformation.PhoneType &&
                                  typeof contactInformation.PhoneType === "object" &&
                                  Object.keys(contactInformation.PhoneType).length > 0,
                              })}
                              name="PhoneNo" autoComplete="off" required maxLength={10}
                              value={contactInformation.PhoneNo || ""}
                              disabled={!contactInformation.PhoneType ||
                                (typeof contactInformation.PhoneType === "object" &&
                                  Object.keys(contactInformation.PhoneType).length === 0)}
                              onChange={handleChangePhoneNumber}
                            />
                          </div>
                          <div className="d-flex mr-2 justify-content-end align-items-center w-100">
                            <button type="button" className="save-button d-flex align-items-center"
                              onClick={handleAddContactInformation}>
                              <FontAwesomeIcon icon={isEditMode ? faEdit : faPlus}
                                style={{ cursor: "pointer", fontSize: "12px", marginRight: "5px" }} />
                              {isEditMode ? "Update Contact Information" : "Add Contact Information"}
                            </button>
                          </div>
                        </div>

                        {/* Contact table */}
                        <GEOContactTable
                          contactList={contactList}
                          setContactInformation={setContactInformation}
                          setEditItemId={setEditItemId}
                          setContactList={setContactList}
                          setIsEditMode={setIsEditMode}
                          setIsContactChangeFields={setIsContactChangeFields}
                          setIsChangeFields={setIsChangeFields}
                          isGoogleLocation
                          contactInformation={contactInformation}
                        />
                      </fieldset>
                    </div>

                    {/* Contact tab save button */}
                    <div className="row">
                      <div className="col-12 p-0">
                        <div className="py-0 px-2 d-flex justify-content-end align-items-center">
                          <div className="d-flex justify-content-end tab-form-row-gap mt-1">
                            <button type="button" className="save-button ml-2"
                              disabled={!isContactChangeFields} onClick={handleContactSave}>
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ════════════════════════════════════════════════════════════
                    DOCUMENTS / SOP TAB  ★ GEO REVAMP — NEW TAB
                    Allows uploading SOP documents, floor plans, hazmat sheets,
                    photos, and other location-specific files. The SOP badge on
                    the Home tab lights up when any SOP document exists here.
                    ════════════════════════════════════════════════════════════ */}
                {showPage === TABS.DOCUMENTS && (
                  <div className="m-1">
                    <fieldset style={{ border: "1px solid gray" }}>

                      {/* Upload panel */}
                      <div className="tab-form-row py-2">
                        {/* <div className="col-1 d-flex align-items-center justify-content-end ml-1">
                          <label className="tab-form-label text-nowrap">Doc Type</label>
                        </div>
                        <div className="col-2 tab-form-row-gap d-flex">
                          <Select
                            options={GEO_DOC_TYPES}
                            placeholder="Select type..."
                            styles={colourStyles}
                            maxMenuHeight={80}
                            value={docUploadState.documentType}
                            onChange={(e) => setDocUploadState((p) => ({ ...p, documentType: e }))}
                            className="w-100"
                          />
                        </div> */}

                        <div className="col-2 d-flex align-items-center" style={{ gap: "6px" }}>
                          <label className="tab-form-label text-nowrap">Name</label>
                          <input
                            type="text"
                            className="form-control py-1 new-input"
                            placeholder="Document name..."
                            maxLength={100}
                            value={docUploadState.documentName}
                            onChange={(e) => setDocUploadState((p) => ({ ...p, documentName: e.target.value }))}
                          />
                        </div>

                        <div className="col-3 d-flex align-items-center" style={{ gap: "6px" }}>
                          <label className="tab-form-label text-nowrap">Notes</label>
                          <input
                            type="text"
                            className="form-control py-1 new-input"
                            placeholder="Optional notes..."
                            maxLength={200}
                            value={docUploadState.notes}
                            onChange={(e) => setDocUploadState((p) => ({ ...p, notes: e.target.value }))}
                          />
                        </div>

                        {/* Upload File — same native picker pattern as WhiteBoardTab */}
                        <div className="col-2 text-field mt-0 d-flex align-items-center">
                          <input
                            type="file"
                            ref={docFileInputRef}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xlsx,.xls"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setDocUploadState((p) => ({
                                ...p,
                                file,
                                documentName: p.documentName || file?.name || "",
                              }));
                            }}
                          />
                        </div>
                      </div>

                      {/* Document list table */}
                      <div className="table-responsive CAD-table mt-2">
                        <DataTable
                          dense
                          columns={documentColumns}
                          data={documentList}
                          customStyles={tableCustomStyles}
                          pagination
                          responsive
                          striped
                          highlightOnHover
                          fixedHeader
                          fixedHeaderScrollHeight="250px"
                          noDataComponent={
                            geoLocationID
                              ? "No documents on file for this location"
                              : "Save this location first to attach documents"
                          }
                          persistTableHead
                        />
                      </div>

                      {/* Save button row — below table like other tabs */}
                      <div className="row">
                        <div className="col-12 p-0">
                          <div className="py-0 px-2 d-flex justify-content-end align-items-center">
                            <div className="d-flex justify-content-end tab-form-row-gap mt-1">
                              <button
                                type="button"
                                className="save-button ml-2"
                                disabled={isUploadingDoc || !geoLocationID}
                                onClick={handleDocumentUpload}
                                title={!geoLocationID ? "Save location first to attach documents" : "Save document"}
                              >
                                {isUploadingDoc ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                    </fieldset>
                  </div>
                )}

                {/* ════════════════════════════════════════════════════════════
                    FLAG HISTORY TAB (unchanged from original)
                    ════════════════════════════════════════════════════════════ */}
                {showPage === TABS.FLAG_HISTORY && (
                  <div className="m-1">
                    <fieldset style={{ border: "1px solid gray" }}>
                      <div className="table-responsive CAD-table mt-2" style={{ position: "sticky" }}>
                        <DataTable
                          dense
                          columns={flagHistoryColumn}
                          data={flagHistoryList}
                          customStyles={tableCustomStyles}

                          pagination
                          responsive
                          striped
                          highlightOnHover
                          fixedHeader
                          selectableRowsHighlight
                          fixedHeaderScrollHeight="190px"
                          persistTableHead
                        />
                      </div>
                    </fieldset>
                  </div>
                )}

              </div>
            </div>
          </div>
        </dialog>
      ) : null}

      {/* Child modals (unchanged) */}
      {openFlagTableModal && (
        <FlagTableModal {...{ openFlagTableModal, setOpenFlagTableModal, geoLocationID, flagName, aptData }} />
      )}
      {openAddFlagModal && (
        <FlagModal {...{
          openAddFlagModal, setOpenAddFlagModal, isGoogleLocation, createLocationPayload,
          geoLocationID, setGeoLocationID, getFlagListRefetch, selectedFlagData,
          setSelectedFlagData, aptData, setAptData, refetchAptSuiteNoData,
        }} />
      )}
      <ModalConfirm
        showModal={showConfirmModal}
        setShowModal={setShowConfirmModal}
        confirmAction={confirmAction === "close" ? "close" : confirmAction === "clear" ? "clear" : "cancel"}
        handleConfirm={handleConfirm}
      />

      {/* ═══════════════════════════════════════════════════════════════
          ADVANCED PREMISE SEARCH MODAL
          ═══════════════════════════════════════════════════════════════ */}









      {openAdvancedSearch ? (
        <>
          <dialog
            className="modal fade"
            style={{ background: "rgba(0,0,0, 0.5)", zIndex: "200" }}
            id="openAdvancedSearchModal"
            tabIndex="-1"
            aria-hidden="true"
            data-backdrop="false"
          >
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content modal-content-cad">
                <div className="modal-body">
                  {/* Header */}
                  <div className="row">
                    <div className="col-12 p-0 pb-2 d-flex justify-content-between align-items-center">
                      <div className="py-0 px-2 d-flex justify-content-between align-items-center">
                        <p
                          className="p-0 m-0 font-weight-medium"
                          style={{
                            fontSize: 18,
                            fontWeight: 500,
                            letterSpacing: 0.5,
                          }}
                        >
                          Advanced Premise Search
                        </p>
                      </div>
                      {/* <button
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 22,
                          color: "#6b7280",
                          padding: "0 6px",
                          lineHeight: 1
                        }}
                        onClick={() => setOpenAdvancedSearch(false)}
                      >
                        ✕
                      </button> */}
                    </div>
                  </div>
                  {/* Footer */}

                  {/* Body */}
                  <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px" }}>
                    <fieldset style={{ border: "1px solid gray" }}>
                      {/* ── Quick Filters ── */}
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
                        Quick Filters
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingBottom: 10, borderBottom: "1px solid #f3f4f6", marginBottom: 12 }}>
                        {[
                          { id: "unverified", label: "⚠ Unverified Addresses", getPreset: () => ({ IsVerified: false }) },
                          { id: "hazmat", label: "☣ Premises with Hazmat", getPreset: () => ({ HazmatOnFile: true }) },
                          {
                            id: "stale",
                            label: "🕒 Stale (12+ months)",
                            getPreset: () => {
                              const d = new Date();
                              d.setFullYear(d.getFullYear() - 1);
                              return { ModifiedBefore: d.toISOString().slice(0, 10) };
                            },
                          },
                          { id: "pip-assembly", label: "📋 Missing PIP — Assembly / Health", getPreset: () => ({ PIPOnFile: false, PremiseTypes: "Assembly" }) },
                        ].map(({ id, label, getPreset }) => {
                          const active = advActiveQuickFilterId === id;
                          return (
                            <button
                              key={id}
                              type="button"
                              onClick={() => handleAdvQuickFilter(id, getPreset())}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 12px",
                                borderRadius: 14,
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: "pointer",
                                fontFamily: "inherit",
                                border: active ? "1px solid #2563eb" : "1px solid #d1d5db",
                                background: active ? "#2563eb" : "#f3f4f6",
                                color: active ? "#fff" : "#374151",
                                boxShadow: active ? "0 1px 2px rgba(37, 99, 235, 0.35)" : "none",
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>

                      {/* ── Filter By ── */}
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
                        Filter By
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "8px 16px", marginBottom: 14 }}>

                        {/* Address Contains */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>Address contains</label>
                          <input
                            className="form-control py-1 new-input"
                            style={{ flex: 1 }}
                            type="text"
                            placeholder="Street, City, Common Name…"
                            value={advFilters.AddressContains}
                            onChange={(e) => handleAdvFilterChange("AddressContains", e.target.value)}
                          />
                        </div>

                        {/* Verified */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>Verified</label>
                          {[
                            { v: null, l: "Any" },
                            { v: true, l: "Yes" },
                            { v: false, l: "No" },
                          ].map(({ v, l }) => (
                            <button key={l} onClick={() => handleAdvFilterChange("IsVerified", v)}
                              style={{ padding: "4px 14px", borderRadius: 4, border: "1px solid #d1d5db", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", background: advFilters.IsVerified === v ? "#0d1b2e" : "#fff", color: advFilters.IsVerified === v ? "#fff" : "#6b7280" }}>
                              {l}
                            </button>
                          ))}
                        </div>

                        {/* Premise Type */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>Premise Type</label>
                          <select
                            className="form-control py-1 new-input"
                            style={{ flex: 1 }}
                            value={advFilters.PremiseTypes}
                            onChange={(e) => handleAdvFilterChange("PremiseTypes", e.target.value)}
                          >
                            <option value="">Any</option>
                            {premiseDropDown.map((p) => (
                              <option key={p.value} value={p.label}>{p.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Fire Zone */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>Fire Zone</label>
                          <select
                            className="form-control py-1 new-input"
                            style={{ flex: 1 }}
                            value={advFilters.FireZone}
                            onChange={(e) => handleAdvFilterChange("FireZone", e.target.value)}
                          >
                            <option value="">Any</option>
                            {fireZoneDropDown.map((z) => (
                              <option key={z.value} value={z.label}>{z.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* PIP On File */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>PIP On File</label>
                          {[
                            { v: null, l: "Any" },
                            { v: true, l: "Yes" },
                            { v: false, l: "No" },
                          ].map(({ v, l }) => (
                            <button key={l} onClick={() => handleAdvFilterChange("PIPOnFile", v)}
                              style={{ padding: "4px 14px", borderRadius: 4, border: "1px solid #d1d5db", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", background: advFilters.PIPOnFile === v ? "#0d1b2e" : "#fff", color: advFilters.PIPOnFile === v ? "#fff" : "#6b7280" }}>
                              {l}
                            </button>
                          ))}
                        </div>

                        {/* Hazmat */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>Hazmat</label>
                          {[
                            { v: null, l: "Any" },
                            { v: true, l: "Yes" },
                            { v: false, l: "No" },
                          ].map(({ v, l }) => (
                            <button key={l} onClick={() => handleAdvFilterChange("HazmatOnFile", v)}
                              style={{ padding: "4px 14px", borderRadius: 4, border: "1px solid #d1d5db", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", background: advFilters.HazmatOnFile === v ? "#0d1b2e" : "#fff", color: advFilters.HazmatOnFile === v ? "#fff" : "#6b7280" }}>
                              {l}
                            </button>
                          ))}
                        </div>

                        {/* Sprinklers */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>Sprinklers</label>
                          {[
                            { v: null, l: "Any" },
                            { v: true, l: "Yes" },
                            { v: false, l: "No" },
                          ].map(({ v, l }) => (
                            <button key={l} onClick={() => handleAdvFilterChange("Sprinklers", v)}
                              style={{ padding: "4px 14px", borderRadius: 4, border: "1px solid #d1d5db", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", background: advFilters.Sprinklers === v ? "#0d1b2e" : "#fff", color: advFilters.Sprinklers === v ? "#fff" : "#6b7280" }}>
                              {l}
                            </button>
                          ))}
                        </div>

                        {/* Has Constraints */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>Has Constraints</label>
                          {[
                            { v: null, l: "Any" },
                            { v: true, l: "Yes" },
                            { v: false, l: "No" },
                          ].map(({ v, l }) => (
                            <button key={l} onClick={() => handleAdvFilterChange("HasConstraints", v)}
                              style={{ padding: "4px 14px", borderRadius: 4, border: "1px solid #d1d5db", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", background: advFilters.HasConstraints === v ? "#0d1b2e" : "#fff", color: advFilters.HasConstraints === v ? "#fff" : "#6b7280" }}>
                              {l}
                            </button>
                          ))}
                        </div>

                        {/* Stories range */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>Stories</label>
                          <input
                            className="form-control py-1 new-input"
                            style={{ width: 70 }}
                            type="number"
                            placeholder="min"
                            value={advFilters.StoriesMin}
                            onChange={(e) => handleAdvFilterChange("StoriesMin", e.target.value)}
                          />
                          <span style={{ color: "#9ca3af", fontSize: 12 }}>to</span>
                          <input
                            className="form-control"
                            style={{ width: 70 }}
                            type="number"
                            placeholder="max"
                            value={advFilters.StoriesMax}
                            onChange={(e) => handleAdvFilterChange("StoriesMax", e.target.value)}
                          />
                        </div>

                        {/* Updated Before */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <label style={{ fontSize: 12, color: "#374151", minWidth: 110, textAlign: "right", fontWeight: 600 }}>Updated before</label>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <DatePicker
                              id="advSearchModifiedBefore"
                              name="advSearchModifiedBefore"
                              selected={parsePremisePipDate(advFilters.ModifiedBefore)}
                              onChange={(date) => {
                                const ymd = date
                                  ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
                                  : "";
                                handleAdvFilterChange("ModifiedBefore", ymd);
                              }}
                              isClearable={!!advFilters.ModifiedBefore}
                              placeholderText="Select Date…"
                              dateFormat="MM/dd/yyyy"
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              showDisabledMonthNavigation
                              autoComplete="off"
                              onKeyDown={(e) => {
                                if (!((e.key >= "0" && e.key <= "9") || e.key === "Backspace" || e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Delete" || e.key === ":" || e.key === "/" || e.key === " " || e.key === "F5")) {
                                  e.preventDefault();
                                }
                              }}
                              className="form-control py-1 new-input w-100"
                            />
                          </div>
                        </div>
                      </div>



                    </fieldset>
                    {/* ── Results header ── */}
                    <div className="row d-flex justify-content-between align-items-center mb-2 mt-2">
                      <div className="col-6 " style={{ color: "#6b7280", fontSize: 14 }}>
                        {advSearched && (
                          <><span style={{ fontWeight: 700, color: "#111827" }}>{advResults.length}</span> matching premise(s)</>
                        )}
                      </div>

                      <div className="col-6 d-flex justify-content-end align-items-center gap-2">
                        <button
                          className="cancel-button mr-2"
                          onClick={handleAdvClearFilters}
                        >
                          Clear filters
                        </button>
                        <button
                          className="save-button mr-2"
                          onClick={() => handleAdvSearch()}
                          disabled={advLoading}
                        >
                          {advLoading ? "Searching…" : "Search"}
                        </button>
                        <button
                          className="cancel-button"
                          onClick={() => setOpenAdvancedSearch(false)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                    {/* ── Results table ── */}
                    <div style={{ border: "1px solid #e5e7eb", borderRadius: 5, overflow: "hidden", background: "#fff" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                        <thead>
                          <tr>
                            {["Address", "Apt/Suite", "Verified", "Premise Type", "Fire Zone", "Flags", "Last Updated"].map((h) => (
                              <th key={h} style={{ background: "#001f3f", color: "#fff", textAlign: "left", padding: "8px 10px", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: ".4px", whiteSpace: "nowrap" }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {advLoading ? (
                            <tr>
                              <td colSpan={7} style={{ padding: "30px 18px", textAlign: "center", color: "#6b7280", fontStyle: "italic" }}>
                                Searching…
                              </td>
                            </tr>
                          ) : !advSearched ? (
                            <tr>
                              <td colSpan={7} style={{ padding: "30px 18px", textAlign: "center", color: "#6b7280" }}>
                                Set filters and click <strong>Search</strong> to find premises.
                              </td>
                            </tr>
                          ) : advResults.length === 0 ? (
                            <tr>
                              <td colSpan={7} style={{ padding: "30px 18px", textAlign: "center", color: "#6b7280", fontStyle: "italic" }}>
                                No premises match these filters. Try clearing some criteria.
                              </td>
                            </tr>
                          ) : advResults.map((row, idx) => {
                            const flags = [];
                            if (row.PIPOnFile) flags.push(<span key="pip" style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0", whiteSpace: "nowrap" }}>📋 PIP</span>);
                            if (row.HazmatOnFile) flags.push(<span key="haz" style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: "#dc2626", color: "#fff", whiteSpace: "nowrap" }}>☣ Haz</span>);
                            if (row.Sprinklers) flags.push(<span key="spr" style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: "#dbeafe", color: "#1e40af", whiteSpace: "nowrap" }}>💧 Spr</span>);
                            if (row.HasConstraints) flags.push(<span key="con" style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: "#fef2f2", color: "#991b1b", border: "1px solid #fca5a5", whiteSpace: "nowrap" }}>⚠ Constraints</span>);
                            const isVerified = row.IsVerified === 1 || row.IsVerified === true || row.IsVerified === "1";
                            return (
                              <tr
                                key={`${row.GeoLocationID}-${row.AptID}-${idx}`}
                                style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer" }}
                                onClick={() => applyAdvancedSearchRow(row)}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#eff6ff"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                              >
                                <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                                  {row.CommonPlace
                                    ? <><strong>{row.CommonPlace}</strong><br /><span style={{ color: "#6b7280" }}>{row.Location || row.Street}</span></>
                                    : (row.Location || row.Street || "—")}
                                </td>
                                <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>{row.ApartmentNo || "—"}</td>
                                <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                                  <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 9, background: isVerified ? "#dcfce7" : "#fee2e2", color: isVerified ? "#166534" : "#991b1b" }}>
                                    {isVerified ? "VERIFIED" : "UNVERIFIED"}
                                  </span>
                                </td>
                                <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>{row.PremiseType || "—"}</td>
                                <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>{row.FireZone || "—"}</td>
                                <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                    {flags.length ? flags : "—"}
                                  </div>
                                </td>
                                <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                                  {row.ModifiedDtTm
                                    ? getShowingWithOutTime(row.ModifiedDtTm)
                                    : row.LastModifiedDtTm
                                      ? getShowingWithOutTime(row.LastModifiedDtTm)
                                      : row.DetailModifiedDtTm
                                        ? getShowingWithOutTime(row.DetailModifiedDtTm)
                                        : (row.CreatedDtTm ? getShowingWithOutTime(row.CreatedDtTm) : "—")}
                                  {(row.ModifiedByUserName || row.CreatedByUserName || row.DetailModifiedByUserName) && (
                                    <><br /><span style={{ color: "#9ca3af", fontSize: 10 }}>by {row.ModifiedByUserName || row.CreatedByUserName || row.DetailModifiedByUserName}</span></>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>




                </div>
              </div>
            </div>
          </dialog>
        </>
      ) : (
        <> </>
      )
      }








    </>
  );
};

export default memo(GEOModal);

// ─── PropTypes ────────────────────────────────────────────────────────────────
GEOModal.propTypes = {
  openGEOModal: PropTypes.bool.isRequired,
  setOpenGEOModal: PropTypes.func.isRequired,
  setSelectedButton: PropTypes.func,
  unVerifiedSingleLocationsList: PropTypes.array,
  refetchUnVerifiedLocationsData: PropTypes.func,
  isVerifiedPage: PropTypes.bool,
  // ★ GEO REVAMP: new prop — callback when location is confirmed
  // CallTaker passes a function here to update its map pin in real time.
  // Receives: { location, latitude, longitude, geoLocationID }
  onLocationConfirmed: PropTypes.func,
};

GEOModal.defaultProps = {
  setSelectedButton: () => { },
  unVerifiedSingleLocationsList: [],
  refetchUnVerifiedLocationsData: () => { },
  isVerifiedPage: false,
  onLocationConfirmed: () => { },
};
